import { Component, StrictMode, useEffect, useMemo, useState, type ErrorInfo, type FormEvent, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { Activity, Bell, BellRing, Boxes, Building2, ChevronDown, CircleDollarSign, CreditCard, Database, Download, Gauge, KeyRound, Menu, Network, PackageOpen, Palette, Plug, Plus, Printer, Radio, RefreshCw, ScrollText, Search, Server, Settings, ShieldCheck, UserCog, Users, Wifi, X } from "lucide-react";

import { LiveConnection, type LiveEvent, type LiveMode } from "./realtime.js";
import { createResource, exportReport, loadCredentialPolicy, loadGuestEnrollments, loadGuestPolicies, loadOperatorPermissions, loadOperatorRoles, loadOperators, loadRadiusLogs, loadReports, loadSubscriberConnections, loadSubscriberPage, loadSupportCases, loadWorkspace, newCorrelationId, revealCredential, runReport, type CredentialPolicy, type DashboardData, type ReportDefinition, type WorkspaceData } from "./api.js";
import { suggestQueue, type QueueForm } from "./package-queue.js";
import { NotificationSettings } from "./notification-settings.js";
import { IntegrationSettings } from "./integration-settings.js";
import { AuditSettings } from "./audit-settings.js";
import "@fontsource/dm-sans/400.css";
import "@fontsource/dm-sans/500.css";
import "@fontsource/dm-sans/600.css";
import "@fontsource/manrope/600.css";
import "@fontsource/manrope/700.css";
import "@fontsource/manrope/800.css";
import "./styles.css";
import "./security.css";
import "./voucher-designer.css";
import "./printers.css";
import "./edge-node.css";

type Page = "overview" | "subscribers" | "packages" | "billing" | "vouchers" | "nas" | "sessions" | "reports" | "support" | "guests" | "admin";
interface Session {
  tenantId: string;
  token: string;
  csrf: string;
}

const nav: readonly {
  page: Page;
  label: string;
  icon: typeof Gauge;
  section: string;
}[] = [
  { page: "overview", label: "Overview", icon: Gauge, section: "Workspace" },
  {
    page: "subscribers",
    label: "Subscribers",
    icon: Users,
    section: "Core CHIS",
  },
  {
    page: "packages",
    label: "Packages & policy",
    icon: Boxes,
    section: "Core CHIS",
  },
  {
    page: "billing",
    label: "Billing",
    icon: CircleDollarSign,
    section: "Finance",
  },
  {
    page: "vouchers",
    label: "Vouchers & POS",
    icon: CreditCard,
    section: "Finance",
  },
  { page: "nas", label: "NAS devices", icon: Server, section: "Network" },
  { page: "sessions", label: "AAA sessions", icon: Radio, section: "Network" },
  { page: "guests", label: "Guest Wi-Fi", icon: Wifi, section: "Operations" },
  { page: "support", label: "Support cases", icon: UserCog, section: "Operations" },
  { page: "reports", label: "Reports", icon: ScrollText, section: "Operations" },
  { page: "admin", label: "Administration", icon: Settings, section: "System" },
];
const pageNames = new Set<Page>(nav.map((item) => item.page));
function pageFromLocation(): Page {
  const value = new URLSearchParams(location.search).get("page") as Page | null;
  return value !== null && pageNames.has(value) ? value : "overview";
}

const subscribers = [
  {
    name: "Amir Rahman",
    username: "amir.home",
    plan: "Home 100",
    status: "Active",
    usage: "67.8 GB",
    avatar: "AR",
  },
  {
    name: "Noor Trading LLC",
    username: "noor.office",
    plan: "Business 300",
    status: "Active",
    usage: "241 GB",
    avatar: "NT",
  },
  {
    name: "Sara Khalid",
    username: "sara.k",
    plan: "Home 50",
    status: "Suspended",
    usage: "12.4 GB",
    avatar: "SK",
  },
  {
    name: "Marina Café",
    username: "marina.guest",
    plan: "Hotspot Pro",
    status: "Pending",
    usage: "—",
    avatar: "MC",
  },
];

function App() {
  const preview = new URLSearchParams(location.search).get("preview") === "1";
  const [session, setSession] = useState<Session | undefined>(preview ? { tenantId: "preview", token: "preview", csrf: "preview" } : undefined);
  const [restoring, setRestoring] = useState(!preview);
  const [page, setPage] = useState<Page>(pageFromLocation);
  const [menu, setMenu] = useState(false);
  const [mode, setMode] = useState<LiveMode>(preview ? "websocket" : "offline");
  const [events, setEvents] = useState<LiveEvent[]>([]);
  const [workspace, setWorkspace] = useState<WorkspaceData>();
  const [dataError, setDataError] = useState(false);
  const [revision, setRevision] = useState(0);
  function navigate(next: Page) {
    setPage(next);
    setMenu(false);
    const url = new URL(location.href);
    if (next === "overview") url.searchParams.delete("page");
    else url.searchParams.set("page", next);
    history.pushState({ page: next }, "", url);
  }
  useEffect(() => {
    const changed = () => setPage(pageFromLocation());
    addEventListener("popstate", changed);
    return () => removeEventListener("popstate", changed);
  }, []);
  useEffect(() => {
    if (session === undefined || preview) return;
    const connection = new LiveConnection({
      tenantId: session.tenantId,
      token: session.token,
      csrf: session.csrf,
      onMode: setMode,
      onEvent: (event) => setEvents((current) => [event, ...current].slice(0, 30)),
    });
    connection.start();
    return () => connection.stop();
  }, [session, preview]);
  useEffect(() => {
    if (session === undefined || preview) return;
    let current = true;
    const refresh = async () => {
      try {
        const data = await loadWorkspace(session.tenantId, session.token);
        if (current) {
          setWorkspace(data);
          setDataError(false);
        }
      } catch {
        if (current) setDataError(true);
      }
    };
    void refresh();
    const timer = window.setInterval(() => void refresh(), 30_000);
    return () => {
      current = false;
      window.clearInterval(timer);
    };
  }, [session, preview, events[0]?.sequence, revision]);
  useEffect(() => {
    if (preview) return;
    let current = true;
    void fetch("/api/v1/sessions/current", { cache: "no-store" })
      .then(async (response) => {
        if (response.ok) {
          const body = (await response.json()) as {
            tenant_id: string;
            csrf_token: string;
          };
          if (current)
            setSession({
              tenantId: body.tenant_id,
              token: "",
              csrf: body.csrf_token,
            });
        }
      })
      .finally(() => {
        if (current) setRestoring(false);
      });
    return () => {
      current = false;
    };
  }, [preview]);
  async function logout() {
    if (session !== undefined && !preview)
      await fetch("/api/v1/sessions/current", {
        method: "DELETE",
        headers: { "x-csrf-token": session.csrf },
      }).catch(() => undefined);
    setSession(undefined);
    setWorkspace(undefined);
    setEvents([]);
    setMode("offline");
  }
  if (restoring)
    return (
      <div className="session-loading">
        <span className="pulse" />
        <strong>Restoring secure session…</strong>
      </div>
    );
  if (session === undefined) return <SignIn onSuccess={setSession} />;
  return (
    <div className="app-shell">
      <Sidebar active={page} open={menu} onNavigate={navigate} onClose={() => setMenu(false)} />
      <div className="app-stage">
        <Header mode={mode} onMenu={() => setMenu(true)} onLogout={() => void logout()} onNavigate={navigate} workspace={workspace} />
        <main id="main-content" className="main-content">
          {dataError && (
            <div className="data-warning" role="status">
              Live data is temporarily unavailable. Retrying automatically.
            </div>
          )}
          <PageContent page={page} liveEvents={events} workspace={workspace} preview={preview} session={session} onNavigate={navigate} onChanged={() => setRevision((value) => value + 1)} />
        </main>
        <AppFooter mode={mode} />
        <MobileNav active={page} onNavigate={navigate} />
      </div>
    </div>
  );
}

function SignIn({ onSuccess }: { onSuccess: (session: Session) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const data = new FormData(event.currentTarget);
    try {
      const response = await fetch("/api/v1/sessions", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          email: data.get("email"),
          password: data.get("password"),
          correlation_id: newCorrelationId(),
        }),
        cache: "no-store",
      });
      if (!response.ok) throw new Error("Unable to sign in");
      const body = (await response.json()) as {
        tenant_id: string;
        csrf_token: string;
      };
      onSuccess({ tenantId: body.tenant_id, token: "", csrf: body.csrf_token });
    } catch {
      setError("The email or password is incorrect.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="signin-page">
      <section className="signin-brand">
        <img src="/vns-logo.png" alt="VNS" />
        <div>
          <span>CHIS</span>
          <h1>Networks, understood.</h1>
          <p>One secure workspace for subscribers, revenue and live network operations.</p>
        </div>
        <div className="signal-grid" aria-hidden="true">
          <i />
          <i />
          <i />
          <i />
          <i />
        </div>
      </section>
      <section className="signin-panel">
        <form onSubmit={(event) => void submit(event)}>
          <div className="product-mark">
            <img src="/vns-logo.png" alt="VNS" />
            <span>CHIS · v4</span>
          </div>
          <p className="eyebrow">Secure workspace</p>
          <h2>Welcome back</h2>
          <p className="muted">Sign in with your VNS-authorized account.</p>
          <label>
            Email address
            <input name="email" type="email" autoComplete="username" placeholder="name@company.com" required />
          </label>
          <label>
            Password
            <input name="password" type="password" autoComplete="current-password" placeholder="Enter your password" required />
          </label>
          {error && (
            <div className="form-error" role="alert">
              {error}
            </div>
          )}
          <button className="primary wide" disabled={busy}>
            {busy ? "Signing in…" : "Sign in securely"}
          </button>
          <p className="signin-note">
            <ShieldCheck size={16} /> Protected by scoped access and audited sessions
          </p>
        </form>
      </section>
    </div>
  );
}

function Sidebar({ active, open, onNavigate, onClose }: { active: Page; open: boolean; onNavigate: (page: Page) => void; onClose: () => void }) {
  const sections = [...new Set(nav.map((item) => item.section))];
  return (
    <>
      <button className={`sidebar-scrim ${open ? "show" : ""}`} onClick={onClose} aria-label="Close navigation" />
      <aside className={`sidebar ${open ? "open" : ""}`}>
        <div className="sidebar-brand">
          <img src="/vns-logo.png" alt="VNS" />
          <div>
            <strong>CHIS</strong>
            <span>Control centre</span>
          </div>
          <button className="icon-button close-menu" onClick={onClose}>
            <X />
          </button>
        </div>
        <nav aria-label="Primary">
          {sections.map((section) => (
            <div className="nav-section" key={section}>
              <p>{section}</p>
              {nav
                .filter((item) => item.section === section)
                .map(({ page, label, icon: Icon }) => (
                  <button key={page} className={active === page ? "active" : ""} onClick={() => onNavigate(page)}>
                    <Icon />
                    <span>{label}</span>
                    {page === "sessions" && <b>128</b>}
                  </button>
                ))}
            </div>
          ))}
        </nav>
        <div className="sidebar-health">
          <div>
            <span className="pulse" />
            <strong>Platform healthy</strong>
          </div>
          <p>API, databases, Redis & AAA operational</p>
        </div>
      </aside>
    </>
  );
}

function Header({ mode, onMenu, onLogout, onNavigate, workspace }: { mode: LiveMode; onMenu: () => void; onLogout: () => void; onNavigate: (page: Page) => void; workspace?: WorkspaceData }) {
  const live = mode === "websocket" || mode === "sse" || mode === "polling";
  const [accountOpen, setAccountOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  useEffect(() => {
    if (!accountOpen && !notificationsOpen && !searchOpen) return;
    const closePopovers = () => {
      setAccountOpen(false);
      setNotificationsOpen(false);
      setSearchOpen(false);
    };
    const dismissOutside = (event: PointerEvent) => {
      if (!(event.target instanceof Element) || event.target.closest(".topbar-popover") === null) closePopovers();
    };
    const dismissWithKeyboard = (event: KeyboardEvent) => {
      if (event.key === "Escape") closePopovers();
    };
    document.addEventListener("pointerdown", dismissOutside);
    document.addEventListener("keydown", dismissWithKeyboard);
    return () => {
      document.removeEventListener("pointerdown", dismissOutside);
      document.removeEventListener("keydown", dismissWithKeyboard);
    };
  }, [accountOpen, notificationsOpen, searchOpen]);
  const searchResults = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (term.length < 2) return [];
    const sources: [Page, Record<string, unknown>[], string[]][] = [
      ["subscribers", workspace?.subscribers ?? [], ["display_name", "username"]],
      ["packages", workspace?.packages ?? [], ["name", "code"]],
      ["nas", workspace?.nas ?? [], ["name", "code", "address"]],
      ["billing", workspace?.billing ?? [], ["invoice_number", "customer"]],
    ];
    return sources
      .flatMap(([page, rows, keys]) =>
        rows
          .filter((row) => keys.some((key) => cell(row, key).toLowerCase().includes(term)))
          .slice(0, 4)
          .map((row) => ({
            page,
            label: keys
              .map((key) => cell(row, key))
              .filter((value) => value !== "—")
              .join(" · "),
          })),
      )
      .slice(0, 10);
  }, [query, workspace]);
  return (
    <header className="topbar">
      <button className="icon-button menu-button" onClick={onMenu} aria-label="Open navigation">
        <Menu />
      </button>
      <button className="scope-picker">
        <span className="scope-logo">V</span>
        <span>
          <small>Workspace</small>
          <strong>VNS Demo · Dubai</strong>
        </span>
        <ChevronDown />
      </button>
      <div className="topbar-actions">
        <div className={`live-status ${live ? "connected" : ""}`}>
          <span />
          <div>
            <small>Realtime</small>
            <strong>{mode === "websocket" ? "Live" : mode === "polling" ? "Polling" : mode === "sse" ? "Live · SSE" : mode}</strong>
          </div>
        </div>
        <div className="topbar-popover">
          <button className="search-button" onClick={() => { setSearchOpen((value) => !value); setNotificationsOpen(false); setAccountOpen(false); }} aria-expanded={searchOpen}>
            <Search />
            <span>Search anything</span>
            <kbd>Ctrl K</kbd>
          </button>
          {searchOpen && (
            <div className="popover search-popover">
              <label>
                <Search />
                <input autoFocus value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Subscribers, packages, NAS, invoices…" />
              </label>
              {query.length < 2 ? (
                <p>Enter at least two characters.</p>
              ) : searchResults.length === 0 ? (
                <p>No matching records.</p>
              ) : (
                searchResults.map((result, index) => (
                  <button
                    key={`${result.page}-${index}`}
                    onClick={() => {
                      onNavigate(result.page);
                      setSearchOpen(false);
                      setQuery("");
                    }}
                  >
                    <strong>{result.label}</strong>
                    <small>Open {result.page}</small>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
        <div className="topbar-popover">
          <button className="icon-button notification" onClick={() => { setNotificationsOpen((value) => !value); setSearchOpen(false); setAccountOpen(false); }} aria-label="Notifications" aria-expanded={notificationsOpen}>
            <Bell />
            <i>3</i>
          </button>
          {notificationsOpen && (
            <div className="popover notification-popover">
              <strong>Notifications</strong>
              <button
                onClick={() => {
                  onNavigate("sessions");
                  setNotificationsOpen(false);
                }}
              >
                AAA reconciliation requires review<small>Network · now</small>
              </button>
              <button
                onClick={() => {
                  onNavigate("billing");
                  setNotificationsOpen(false);
                }}
              >
                Invoice is overdue<small>Finance · 8 min</small>
              </button>
              <button
                onClick={() => {
                  onNavigate("nas");
                  setNotificationsOpen(false);
                }}
              >
                NAS health degraded<small>Network · 14 min</small>
              </button>
            </div>
          )}
        </div>
        <div className="topbar-popover">
          <button className="avatar-button" onClick={() => { setAccountOpen((value) => !value); setSearchOpen(false); setNotificationsOpen(false); }} aria-expanded={accountOpen}>
            <span>AK</span>
            <div>
              <strong>Ahsan Khan</strong>
              <small>Tenant administrator</small>
            </div>
            <ChevronDown />
          </button>
          {accountOpen && (
            <div className="popover account-popover">
              <button
                onClick={() => {
                  onNavigate("admin");
                  setAccountOpen(false);
                }}
              >
                Profile & administration
              </button>
              <button onClick={onLogout}>Sign out</button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

function AppFooter({ mode }: { mode: LiveMode }) {
  const [statusOpen, setStatusOpen] = useState(false);
  const [health, setHealth] = useState<{
    status: string;
    checks: Record<string, string>;
  }>();
  const [version, setVersion] = useState("Loading…");
  useEffect(() => {
    void fetch("/api/v1/version", { cache: "no-store" })
      .then((response) => response.json())
      .then((body: { version: string }) => setVersion(body.version))
      .catch(() => setVersion("Unavailable"));
  }, []);
  async function toggleStatus() {
    const next = !statusOpen;
    setStatusOpen(next);
    if (next)
      await fetch("/health/ready", { cache: "no-store" })
        .then((response) => response.json())
        .then(setHealth)
        .catch(() =>
          setHealth({
            status: "unavailable",
            checks: { process: "unavailable" },
          }),
        );
  }
  const healthy = health?.status === "ready";
  return (
    <footer className="app-footer">
      <div className="footer-brand">
        <strong>CHIS-v4</strong>
        <span>VNS Cloud Infrastructure</span>
      </div>
      <nav aria-label="Footer">
        <div className="status-control">
          <button className={`footer-status ${mode !== "offline" ? "online" : ""}`} onClick={() => void toggleStatus()} data-tooltip="View live API, databases, FreeRADIUS, Redis and realtime status">
            <i />
            {mode !== "offline" ? "Systems connected" : "Connection unavailable"}
          </button>
          {statusOpen && (
            <section className="status-popup" role="dialog" aria-label="System status">
              <header>
                <div>
                  <span className={`status-orb ${healthy ? "healthy" : "warning"}`} />
                  <div>
                    <strong>{healthy ? "All systems operational" : "System attention required"}</strong>
                    <small>Live service readiness</small>
                  </div>
                </div>
                <button className="icon-button" onClick={() => setStatusOpen(false)} aria-label="Close status">
                  <X />
                </button>
              </header>
              <div className="status-services">
                {Object.entries(
                  health?.checks ?? {
                    process: "checking",
                    database: "checking",
                    radius_database: "checking",
                    redis: "checking",
                  },
                ).map(([name, value]) => (
                  <div key={name}>
                    <span>
                      <i className={value === "ok" ? "ok" : "warn"} />
                      <strong>{name === "process" ? "CHIS API" : name === "radius_database" ? "RADIUS database" : name[0]!.toUpperCase() + name.slice(1)}</strong>
                    </span>
                    <b>{value === "ok" ? "Operational" : value}</b>
                  </div>
                ))}
                <div>
                  <span>
                    <i className={mode !== "offline" ? "ok" : "warn"} />
                    <strong>Realtime</strong>
                  </span>
                  <b>{mode}</b>
                </div>
              </div>
              <footer>
                <span>Automatically refreshed when opened</span>
                <a href="/health/ready" target="_blank" rel="noreferrer">
                  View JSON
                </a>
              </footer>
            </section>
          )}
        </div>
        <a href="mailto:support@vns.ae" data-tooltip="Email VNS technical support">
          Support
        </a>
      </nav>
      <div className="footer-version" data-tooltip="Currently installed CHIS-v4 release">
        <small>Installed version</small>
        <strong>{version}</strong>
      </div>
    </footer>
  );
}

function PageContent({ page, liveEvents, workspace, preview, session, onNavigate, onChanged }: { page: Page; liveEvents: LiveEvent[]; workspace?: WorkspaceData; preview: boolean; session: Session; onNavigate: (page: Page) => void; onChanged: () => void }) {
  const [creating, setCreating] = useState<CreatePage>();
  if (page === "overview") return <Overview liveEvents={liveEvents} dashboard={workspace?.dashboard} onNavigate={onNavigate} />;
  const definitions: Record<
    Exclude<Page, "overview">,
    {
      eyebrow: string;
      title: string;
      description: string;
      action?: string;
      icon: typeof Users;
    }
  > = {
    subscribers: {
      eyebrow: "Core CHIS",
      title: "Subscribers",
      description: "Manage subscriber identity, access, package assignment and lifecycle.",
      action: "New subscriber",
      icon: Users,
    },
    packages: {
      eyebrow: "Catalogue",
      title: "Packages & policy",
      description: "Publish immutable service plans and normalized network policies.",
      action: "Create package",
      icon: Boxes,
    },
    billing: {
      eyebrow: "Finance",
      title: "Billing & payments",
      description: "Issue invoices, allocate payments and preserve an immutable ledger.",
      action: "New invoice",
      icon: CircleDollarSign,
    },
    vouchers: {
      eyebrow: "Point of sale",
      title: "Vouchers & POS",
      description: "Generate secure batches, complete sales and control reversals.",
      action: "Generate batch",
      icon: CreditCard,
    },
    nas: {
      eyebrow: "Network",
      title: "NAS devices",
      description: "Operate generic RADIUS and MikroTik access infrastructure.",
      action: "Add NAS",
      icon: Server,
    },
    sessions: {
      eyebrow: "Live network",
      title: "AAA sessions",
      description: "Follow authorization, usage and accounting reconciliation in real time.",
      action: "Export view",
      icon: Radio,
    },
    reports: {eyebrow:"Insights",title:"Reports",description:"Run curated, permission-checked operational and financial reports.",icon:ScrollText},
    support: {eyebrow:"Operations",title:"Support cases",description:"Manage complaint intake, assignment, SLA and resolution timelines.",icon:UserCog},
    guests: {eyebrow:"Guest access",title:"Guest Wi-Fi",description:"Sponsor verified, time-bounded visitor network access.",icon:Wifi},
    admin: {
      eyebrow: "Platform",
      title: "Settings",
      description: "Configure this CHIS instance, workspace, operations, security and integrations.",
      icon: Settings,
    },
  };
  const definition = definitions[page];
  const creatable = page === "subscribers" || page === "packages" || page === "billing" || page === "vouchers" || page === "nas";
  return (
    <>
      <PageHeader
        {...definition}
        onAction={creatable ? () => setCreating(page as CreatePage) : undefined}
        {...(page === "billing"
          ? {
              secondaryAction: "Record payment",
              onSecondary: () => setCreating("payments"),
            }
          : {})}
      />
      <PageErrorBoundary key={page}>
        <ModulePage page={page} data={workspace} preview={preview} session={session} onChanged={onChanged} />
      </PageErrorBoundary>
      {creating && (
        <CreateDialog
          page={creating}
          workspace={workspace}
          session={session}
          onClose={() => setCreating(undefined)}
          onCreated={() => {
            setCreating(undefined);
            onChanged();
          }}
        />
      )}
    </>
  );
}

function money(minor: number | undefined) {
  return new Intl.NumberFormat("en-AE", {
    style: "currency",
    currency: "AED",
    maximumFractionDigits: 0,
  }).format((minor ?? 0) / 100);
}
function dataSize(bytes: number | undefined) {
  const units=["B","KB","MB","GB","TB"];
  let scaled=Math.max(0,bytes??0),index=0;
  while(scaled>=1024&&index<units.length-1){scaled/=1024;index++;}
  return `${scaled.toLocaleString("en-AE",{maximumFractionDigits:index===0?0:1})} ${units[index]}`;
}

class PageErrorBoundary extends Component<{children:ReactNode},{error:Error|null}> {
  public state:{error:Error|null}={error:null};
  public static getDerivedStateFromError(error:Error){return{error};}
  public componentDidCatch(error:Error,info:ErrorInfo){console.error("CHIS page rendering failed",error,info.componentStack);}
  public render(){
    if(this.state.error===null)return this.props.children;
    return <section className="panel page-failure" role="alert"><span><Activity/></span><div><p className="eyebrow">Page unavailable</p><h2>This page could not be displayed</h2><p>CHIS contained the problem so the rest of the application remains available. Retry the page, or return to the dashboard if it continues.</p><details><summary>Diagnostic details</summary><code>{this.state.error.message||"Unexpected page rendering error"}</code></details><footer><button className="secondary" type="button" onClick={()=>window.location.assign("/?page=overview")}>Go to dashboard</button><button className="primary" type="button" onClick={()=>window.location.reload()}>Retry page</button></footer></div></section>;
  }
}
function Overview({ liveEvents, dashboard, onNavigate }: { liveEvents: LiveEvent[]; dashboard?: DashboardData; onNavigate:(page:Page)=>void }) {
  const active=dashboard?.nas.active??0,total=dashboard?.nas.total??0,disabled=dashboard?.nas.disabled??0;
  const healthyPercent=total===0?0:Math.round(active/total*100);
  const subscriberPercent=dashboard?.subscribers?Math.round(dashboard.active_subscribers/dashboard.subscribers*100):0;
  return (
    <>
      <div className="welcome-row">
        <div>
          <p className="eyebrow">{new Intl.DateTimeFormat("en-AE",{weekday:"long",day:"numeric",month:"long"}).format(new Date())}</p>
          <h1>Network overview</h1>
          <p>Here is what is happening across your network right now.</p>
        </div>
        <button className="primary" onClick={()=>onNavigate("subscribers")}>
          <Plus /> Quick action
        </button>
      </div>
      <div className="kpi-grid">
        <Kpi label="Active subscribers" value={(dashboard?.active_subscribers ?? 0).toLocaleString()} delta={`${dashboard?.subscribers ?? 0} total`} icon={<Users />} tone="blue" chart={`${subscriberPercent}%`} />
        <Kpi label="Online now" value={(dashboard?.online_sessions ?? 0).toLocaleString()} delta="active AAA sessions" icon={<Wifi />} tone="cyan" chart={`${dashboard?.active_subscribers?Math.min(100,Math.round(dashboard.online_sessions/dashboard.active_subscribers*100)):0}%`} />
        <Kpi label="Monthly revenue" value={money(dashboard?.monthly_revenue_minor)} delta="received this month" icon={<CircleDollarSign />} tone="green" chart="100%" />
        <Kpi label="Open balance" value={money(dashboard?.open_balance_minor)} delta={`${dashboard?.open_invoices ?? 0} invoices`} icon={<CreditCard />} tone="amber" chart={`${dashboard?.open_invoices?100:0}%`} />
      </div>
      <div className="dashboard-grid">
        <section className="panel traffic-panel">
          <PanelHeader title="Network traffic" subtitle="Last 24 hours · all sites" action="View sessions" onAction={()=>onNavigate("sessions")} />
          <div className="traffic-legend">
            <span>
              <i className="download" /> Download <strong>{dataSize(dashboard?.traffic_24h.download_bytes)}</strong>
            </span>
            <span>
              <i className="upload" /> Upload <strong>{dataSize(dashboard?.traffic_24h.upload_bytes)}</strong>
            </span>
          </div>
          <TrafficChart download={dashboard?.traffic_24h.download_bytes??0} upload={dashboard?.traffic_24h.upload_bytes??0} />
        </section>
        <section className="panel health-panel">
          <PanelHeader title="Network health" subtitle="Live NAS status" action="View all" onAction={()=>onNavigate("nas")} />
          <HealthRing percent={healthyPercent} />
          <div className="health-list">
            <Health label="Active" value={String(active)} color="green" />
            <Health label="Disabled" value={String(disabled)} color="amber" />
            <Health label="Unavailable" value={String(Math.max(0,total-active-disabled))} color="red" />
          </div>
        </section>
        <section className="panel subscribers-panel">
          <PanelHeader title="Recent subscribers" subtitle="Latest account activity" action="View all" onAction={()=>onNavigate("subscribers")} />
          <SubscriberRows rows={dashboard?.recent_subscribers??[]} onOpen={()=>onNavigate("subscribers")} />
        </section>
        <section className="panel activity-panel">
          <PanelHeader title="Live activity" subtitle={`${liveEvents.length} events received this session`} action="Audit log" onAction={()=>onNavigate("admin")} />
          <LiveActivityFeed events={liveEvents} />
        </section>
      </div>
    </>
  );
}

function PageHeader({ eyebrow, title, description, action, icon: Icon, onAction, secondaryAction, onSecondary }: { eyebrow: string; title: string; description: string; action?: string; icon: typeof Users; onAction?: () => void; secondaryAction?: string; onSecondary?: () => void }) {
  return (
    <div className="page-header">
      <div className="title-icon">
        <Icon />
      </div>
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {secondaryAction && (
        <button className="secondary" onClick={onSecondary}>
          <CreditCard /> {secondaryAction}
        </button>
      )}
      {action && (
        <button className="primary" onClick={onAction} disabled={onAction === undefined}>
          <Plus /> {action}
        </button>
      )}
    </div>
  );
}

function cell(row: Record<string, unknown>, key: string) {
  const value = row[key];
  return value === null || value === undefined ? "—" : String(value);
}
function ReportsPage({data,session}:{data:WorkspaceData;session:Session}){const[definitions,setDefinitions]=useState<ReportDefinition[]>([]),[selected,setSelected]=useState(""),[rows,setRows]=useState<Record<string,unknown>[]>([]),[fields,setFields]=useState<string[]>([]),[error,setError]=useState(""),[busy,setBusy]=useState(false),today=new Date().toISOString().slice(0,10),monthAgo=new Date(Date.now()-30*86400000).toISOString().slice(0,10);useEffect(()=>{void loadReports(session.tenantId,session.token).then(value=>{setDefinitions(value);setSelected(value[0]?.id??"");}).catch(reason=>setError(reason instanceof Error?reason.message:"Unable to load reports"));},[session.tenantId,session.token]);async function execute(event:FormEvent<HTMLFormElement>){event.preventDefault();setBusy(true);setError("");const form=new FormData(event.currentTarget);try{const result=await runReport(session.tenantId,session.token,session.csrf,selected,{siteId:String(form.get("site_id")??"")||undefined,from:`${form.get("from")}T00:00:00.000Z`,to:`${form.get("to")}T23:59:59.999Z`,limit:500});setRows(result.rows);setFields(result.definition.fields);}catch(reason){setError(reason instanceof Error?reason.message:"Unable to run report");}finally{setBusy(false);}}async function download(format:"csv"|"json"|"xlsx"|"pdf"|"html"){const form=document.querySelector<HTMLFormElement>("#report-run-form");if(!form)return;const value=new FormData(form);try{const result=await exportReport(session.tenantId,session.token,session.csrf,selected,{siteId:String(value.get("site_id")??"")||undefined,from:`${value.get("from")}T00:00:00.000Z`,to:`${value.get("to")}T23:59:59.999Z`,format}),url=URL.createObjectURL(result.blob),anchor=document.createElement("a");anchor.href=url;anchor.download=result.filename;anchor.click();URL.revokeObjectURL(url);}catch(reason){setError(reason instanceof Error?reason.message:"Unable to export report");}}return <><form id="report-run-form" className="panel credential-policy-form" onSubmit={event=>void execute(event)}><div className="field-pair"><label>Report<select value={selected} onChange={event=>setSelected(event.target.value)}>{definitions.map(item=><option key={item.id} value={item.id}>{item.id} · {item.name}</option>)}</select></label><label>Site<select name="site_id"><option value="">All sites</option>{data.sites.map(site=><option key={cell(site,"id")} value={cell(site,"id")}>{cell(site,"name")}</option>)}</select></label><label>From<input name="from" type="date" defaultValue={monthAgo} required/></label><label>To<input name="to" type="date" defaultValue={today} required/></label></div><footer><span>{error&&<p className="form-error">{error}</p>}</span><button className="secondary" type="button" disabled={!selected} onClick={()=>void download("csv")}><Download/> CSV</button><button className="secondary" type="button" disabled={!selected} onClick={()=>void download("json")}><Download/> JSON</button><button className="secondary" type="button" disabled={!selected} onClick={()=>void download("xlsx")}><Download/> XLSX</button><button className="secondary" type="button" disabled={!selected} onClick={()=>void download("pdf")}><Download/> PDF</button><button className="secondary" type="button" disabled={!selected} onClick={()=>void download("html")}><Printer/> Print</button><button className="primary" disabled={busy||!selected}>{busy?"Running…":"Run report"}</button></footer></form>{fields.length>0&&<div className="panel table-panel settings-table"><DataTable headers={fields.map(field=>field.replaceAll("_"," "))} rows={rows.map(row=>fields.map(field=>cell(row,field)))}/></div>}</>}
function SupportPage({data,session}:{data:WorkspaceData;session:Session}){const[rows,setRows]=useState<Record<string,unknown>[]>([]),[search,setSearch]=useState(""),[error,setError]=useState(""),[showForm,setShowForm]=useState(false);async function load(){try{setRows((await loadSupportCases(session.tenantId,session.token,search)).data);}catch(reason){setError(reason instanceof Error?reason.message:"Unable to load cases");}}useEffect(()=>{const timer=setTimeout(()=>void load(),250);return()=>clearTimeout(timer);},[session.tenantId,session.token,search]);async function create(event:FormEvent<HTMLFormElement>){event.preventDefault();const form=new FormData(event.currentTarget);try{await createResource("/api/v1/support/cases",session.tenantId,session.token,{site_id:String(form.get("site_id")),subscriber_id:String(form.get("subscriber_id")||"")||undefined,priority:String(form.get("priority")),category:String(form.get("category")),subject:String(form.get("subject")),body:String(form.get("body"))},session.csrf);setShowForm(false);await load();}catch(reason){setError(reason instanceof Error?reason.message:"Unable to create case");}}return <><div className="panel table-panel"><div className="toolbar"><label className="search"><Search/><input value={search} onChange={event=>setSearch(event.target.value)} placeholder="Search case, subscriber or subject"/></label><button className="primary" onClick={()=>setShowForm(value=>!value)}><Plus/> New case</button></div>{error&&<p className="form-error">{error}</p>}<DataTable headers={["Case","Subject","Subscriber","Priority","Status","Assignee","SLA"]} rows={rows.map(row=>[`#${cell(row,"case_number")}`,cell(row,"subject"),cell(row,"subscriber_username"),cell(row,"priority"),cell(row,"status"),cell(row,"assignee_name"),cell(row,"response_sla_breached")==="true"||cell(row,"resolution_sla_breached")==="true"?"Breached":"On track"])}/></div>{showForm&&<form className="panel credential-policy-form" onSubmit={event=>void create(event)}><div className="field-pair"><label>Site<select name="site_id" required>{data.sites.map(site=><option key={cell(site,"id")} value={cell(site,"id")}>{cell(site,"name")}</option>)}</select></label><label>Subscriber<select name="subscriber_id"><option value="">No subscriber</option>{data.subscribers.map(item=><option key={cell(item,"id")} value={cell(item,"id")}>{cell(item,"username")}</option>)}</select></label><label>Priority<select name="priority" defaultValue="normal"><option>low</option><option>normal</option><option>high</option><option>urgent</option></select></label><label>Category<input name="category" required maxLength={80}/></label></div><label>Subject<input name="subject" required minLength={2} maxLength={200}/></label><label>Details<textarea name="body" required minLength={2} maxLength={10000}/></label><footer><button className="secondary" type="button" onClick={()=>setShowForm(false)}>Cancel</button><button className="primary">Create case</button></footer></form>}</>}
function GuestsPage({session}:{session:Session}){const[verificationCode,setVerificationCode]=useState("");const[policies,setPolicies]=useState<Record<string,unknown>[]>([]),[rows,setRows]=useState<Record<string,unknown>[]>([]),[challenge,setChallenge]=useState<{enrollmentId:string;challengeId:string;challengeCode?:string;deliveryStatus?:string}|null>(null),[credential,setCredential]=useState<{username:string;credential:string;expiresAt:string}|null>(null),[error,setError]=useState("");async function load(){try{const[p,e]=await Promise.all([loadGuestPolicies(session.tenantId,session.token),loadGuestEnrollments(session.tenantId,session.token)]);setPolicies(p);setRows(e.data);}catch(reason){setError(reason instanceof Error?reason.message:"Unable to load guest access");}}useEffect(()=>{void load();},[session.tenantId,session.token]);async function sponsor(event:FormEvent<HTMLFormElement>){event.preventDefault();const form=new FormData(event.currentTarget),policy=policies.find(item=>cell(item,"id")===String(form.get("policy_id")));try{const value=await createResource<{enrollmentId:string;challengeId:string;challengeCode?:string;deliveryStatus?:string}>("/api/v1/guest/enrollments",session.tenantId,session.token,{policy_id:String(form.get("policy_id")),contact_type:String(form.get("contact_type")),contact:String(form.get("contact")),guest_name:String(form.get("guest_name")),consent_version:cell(policy??{},"consent_version"),validity_minutes:Number(form.get("validity_minutes"))},session.csrf);setChallenge(value);setVerificationCode("");await load();}catch(reason){setError(reason instanceof Error?reason.message:"Unable to sponsor guest");}}async function verify(){if(!challenge)return;try{const value=await createResource<{username:string;credential:string;expiresAt:string}>("/api/v1/guest/verify",session.tenantId,session.token,{enrollment_id:challenge.enrollmentId,challenge_id:challenge.challengeId,code:challenge.challengeCode??verificationCode},session.csrf);setCredential(value);setChallenge(null);await load();}catch(reason){setError(reason instanceof Error?reason.message:"Unable to verify guest");}}return <><form className="panel credential-policy-form" onSubmit={event=>void sponsor(event)}><div className="field-pair"><label>Policy<select name="policy_id" required>{policies.filter(item=>cell(item,"status")==="active").map(item=><option key={cell(item,"id")} value={cell(item,"id")}>{cell(item,"name")} · {cell(item,"site_name")}</option>)}</select></label><label>Contact type<select name="contact_type"><option value="phone">Phone</option><option value="email">Email</option></select></label><label>Contact<input name="contact" required/></label><label>Guest name<input name="guest_name" required/></label><label>Validity minutes<input name="validity_minutes" type="number" min="15" max="43200" defaultValue="120" required/></label></div>{error&&<p className="form-error">{error}</p>}<footer><span/><button className="primary" disabled={policies.length===0}>Sponsor guest</button></footer></form>{challenge&&<div className="panel connector-token"><div><strong>Verification challenge · shown once</strong><small>{challenge.deliveryStatus==="queued"?"A verification code is queued for the guest. Enter the code they receive.":`Deliver code ${challenge.challengeCode} to the guest contact, then confirm verification.`}</small></div>{challenge.deliveryStatus==="queued"&&<input aria-label="Guest verification code" inputMode="numeric" maxLength={6} value={verificationCode} onChange={event=>setVerificationCode(event.target.value.replace(/\D/g,""))}/>}<button className="primary" disabled={challenge.deliveryStatus==="queued"&&verificationCode.length!==6} onClick={()=>void verify()}>Verify code</button></div>}{credential&&<div className="panel connector-token"><div><strong>Guest credentials · shown once</strong><code>Username: {credential.username}{"\n"}Password: {credential.credential}{"\n"}Expires: {new Date(credential.expiresAt).toLocaleString()}</code></div></div>}<div className="panel table-panel"><DataTable headers={["Guest","Contact","Policy","Site","Validity","Status","Expires"]} rows={rows.map(row=>[cell(row,"guest_name"),cell(row,"contact_masked"),cell(row,"policy_name"),cell(row,"site_name"),`${cell(row,"requested_validity_minutes")} min`,cell(row,"status"),cell(row,"expires_at")?new Date(cell(row,"expires_at")).toLocaleString():"—"])}/></div></>}
function ExpensePanel({data,session,onChanged}:{data:WorkspaceData;session:Session;onChanged:()=>void}){const[show,setShow]=useState(false),[error,setError]=useState("");async function create(event:FormEvent<HTMLFormElement>){event.preventDefault();const form=new FormData(event.currentTarget);try{await createResource("/api/v1/expenses",session.tenantId,session.token,{site_id:String(form.get("site_id")),occurred_on:String(form.get("occurred_on")),category:String(form.get("category")),payee:String(form.get("payee")),reference:String(form.get("reference")||"")||undefined,amount_minor:Math.round(Number(form.get("amount"))*100),currency:String(form.get("currency")),evidence_reference:String(form.get("evidence_reference")||"")||undefined,idempotency_key:newCorrelationId()},session.csrf);setShow(false);onChanged();}catch(reason){setError(reason instanceof Error?reason.message:"Unable to record expense");}}async function transition(id:string,to:"approved"|"rejected"|"void"){const reason=window.prompt(`${to[0]!.toUpperCase()+to.slice(1)} reason`);if(!reason||reason.trim().length<3)return;try{await createResource(`/api/v1/expenses/${id}/transitions`,session.tenantId,session.token,{to,reason:reason.trim()},session.csrf);onChanged();}catch(value){setError(value instanceof Error?value.message:"Unable to update expense");}}return <div className="panel table-panel secondary-records"><PanelHeader title="Expense register" subtitle="Immutable evidence and approval history" action=""/><div className="toolbar"><span/ ><button className="primary" onClick={()=>setShow(value=>!value)}><Plus/>Record expense</button></div>{error&&<p className="form-error">{error}</p>}{show&&<form className="credential-policy-form" onSubmit={event=>void create(event)}><div className="field-pair"><label>Site<select name="site_id" required>{data.sites.map(site=><option key={cell(site,"id")} value={cell(site,"id")}>{cell(site,"name")}</option>)}</select></label><label>Date<input name="occurred_on" type="date" defaultValue={new Date().toISOString().slice(0,10)} required/></label><label>Category<input name="category" maxLength={80} required/></label><label>Payee<input name="payee" maxLength={200} required/></label><label>Reference<input name="reference" maxLength={200}/></label><label>Amount<input name="amount" type="number" min="0.01" step="0.01" required/></label><label>Currency<input name="currency" defaultValue="AED" pattern="[A-Z]{3}" required/></label><label>Evidence reference<input name="evidence_reference" maxLength={1000}/></label></div><footer><button type="button" className="secondary" onClick={()=>setShow(false)}>Cancel</button><button className="primary">Submit expense</button></footer></form>}<DataTable headers={["Date","Category","Payee","Reference","Amount","Site","Status","Actions"]} rows={data.expenses.map(row=>[cell(row,"occurred_on"),cell(row,"category"),cell(row,"payee"),cell(row,"reference"),`${cell(row,"currency")} ${(Number(cell(row,"amount_minor"))/100).toFixed(2)}`,cell(row,"site"),cell(row,"status"),<span key={cell(row,"id")}>{cell(row,"status")==="submitted"&&<><button className="row-action" onClick={()=>void transition(cell(row,"id"),"approved")}>Approve</button><button className="row-action destructive" onClick={()=>void transition(cell(row,"id"),"rejected")}>Reject</button></>}{cell(row,"status")==="approved"&&<button className="row-action destructive" onClick={()=>void transition(cell(row,"id"),"void")}>Void</button>}</span>])}/></div>;}

function ModulePage({ page, data, preview, session, onChanged }: { page: Exclude<Page, "overview">; data?: WorkspaceData; preview: boolean; session: Session; onChanged: () => void }) {
  const [search, setSearch] = useState("");
  const [subscriberRows,setSubscriberRows]=useState<Record<string,unknown>[]>([]);
  const [subscriberTotal,setSubscriberTotal]=useState(0);
  const [subscriberOffset,setSubscriberOffset]=useState(0);
  const [subscriberStatus,setSubscriberStatus]=useState("");
  const [subscriberPackage,setSubscriberPackage]=useState("");
  const [subscriberSite,setSubscriberSite]=useState("");
  const [subscriberLoading,setSubscriberLoading]=useState(false);
  const [subscriberError,setSubscriberError]=useState("");
  const [subscriberRefresh,setSubscriberRefresh]=useState(0);
  const [selectedSubscriber, setSelectedSubscriber] = useState<Record<string, unknown>>();
  const [selectedResource, setSelectedResource] = useState<Record<string, unknown>>();
  const [selectedAaaSession, setSelectedAaaSession] = useState<Record<string, unknown>>();
  const subscriberLimit=25;
  useEffect(()=>{
    if(page!=="subscribers"||data===undefined)return;
    let active=true;
    const timer=window.setTimeout(()=>{
      setSubscriberLoading(true);setSubscriberError("");
      void loadSubscriberPage(session.tenantId,session.token,{limit:subscriberLimit,offset:subscriberOffset,search:search.trim(),status:subscriberStatus,packageId:subscriberPackage,siteId:subscriberSite})
        .then(result=>{if(active){setSubscriberRows(result.data);setSubscriberTotal(result.total);}})
        .catch(reason=>{if(active)setSubscriberError(reason instanceof Error?reason.message:"Unable to load subscribers");})
        .finally(()=>{if(active)setSubscriberLoading(false);});
    },300);
    return()=>{active=false;window.clearTimeout(timer);};
  },[page,data,session.tenantId,session.token,search,subscriberOffset,subscriberStatus,subscriberPackage,subscriberSite,subscriberRefresh]);
  useEffect(()=>{
    if(selectedSubscriber===undefined)return;
    const latest=subscriberRows.find(row=>cell(row,"id")===cell(selectedSubscriber,"id"));
    if(latest!==undefined&&latest!==selectedSubscriber)setSelectedSubscriber(latest);
  },[subscriberRows,selectedSubscriber]);
  if(page==="reports"&&data!==undefined)return <ReportsPage data={data} session={session}/>;
  if(page==="support"&&data!==undefined)return <SupportPage data={data} session={session}/>;
  if(page==="guests"&&data!==undefined)return <GuestsPage session={session}/>;
  if (page === "subscribers" && data !== undefined) {
    const changed=()=>{setSubscriberRefresh(value=>value+1);onChanged();};
    return (
      <>
        <div className="panel table-panel">
          <SubscriberToolbar search={search} status={subscriberStatus} packageId={subscriberPackage} siteId={subscriberSite} packages={data.packages} sites={data.sites} onSearch={value=>{setSearch(value);setSubscriberOffset(0);}} onStatus={value=>{setSubscriberStatus(value);setSubscriberOffset(0);}} onPackage={value=>{setSubscriberPackage(value);setSubscriberOffset(0);}} onSite={value=>{setSubscriberSite(value);setSubscriberOffset(0);}}/>
          {subscriberError!==""&&<p className="table-error">{subscriberError}</p>}
          <SubscriberTable rows={subscriberRows} session={session} onSelect={setSelectedSubscriber} onChanged={changed} />
          <TablePagination offset={subscriberOffset} limit={subscriberLimit} total={subscriberTotal} loading={subscriberLoading} onPage={setSubscriberOffset}/>
        </div>
        {selectedSubscriber && (
          <SubscriberDrawer
            subscriber={selectedSubscriber}
            packages={data.packages}
            session={session}
            onClose={() => setSelectedSubscriber(undefined)}
            onMacReset={changed}
            onChanged={() => {
              setSelectedSubscriber(undefined);
              changed();
            }}
          />
        )}
      </>
    );
  }
  if (page === "subscribers")
    return (
      <div className="panel table-panel">
        <Toolbar placeholder="Search subscriber, username or phone" filters={["All status", "All packages", "All sites"]} onSearch={setSearch} />
        {preview ? <SubscriberRows /> : <EmptyState />}
      </div>
    );
  if ((page === "packages" || page === "nas") && data !== undefined) {
    const source = page === "packages" ? data.packages : data.nas;
    const visible = source.filter(
      (row) =>
        search.trim() === "" ||
        Object.values(row).some((value) =>
          String(value ?? "")
            .toLowerCase()
            .includes(search.trim().toLowerCase()),
        ),
    );
    return (
      <>
        <div className="panel table-panel">
          <Toolbar placeholder={`Search ${page}`} filters={["All status", "All sites"]} onSearch={setSearch} />
          <ResourceTable kind={page} rows={visible} session={session} onSelect={setSelectedResource} onChanged={onChanged} />
        </div>
        {selectedResource && (
          <ResourceDrawer
            kind={page}
            resource={selectedResource}
            session={session}
            onClose={() => setSelectedResource(undefined)}
            onChanged={() => {
              setSelectedResource(undefined);
              onChanged();
            }}
          />
        )}
      </>
    );
  }
  if (page === "sessions" && data !== undefined) {
    const visible = data.sessions.filter(
      (row) =>
        search.trim() === "" ||
        Object.values(row).some((value) =>
          String(value ?? "")
            .toLowerCase()
            .includes(search.trim().toLowerCase()),
        ),
    );
    return (
      <>
        <div className="panel table-panel">
          <Toolbar placeholder="Search principal, NAS or session ID" filters={["All status", "All sites"]} onSearch={setSearch} />
          <AaaSessionTable rows={visible} onSelect={setSelectedAaaSession} />
        </div>
        {selectedAaaSession && (
          <AaaSessionDrawer
            value={selectedAaaSession}
            session={session}
            onClose={() => setSelectedAaaSession(undefined)}
            onChanged={() => {
              setSelectedAaaSession(undefined);
              onChanged();
            }}
          />
        )}
      </>
    );
  }
  const content = {
    packages: [
      ["Home 100", "AED 100.00", "100 / 20 Mbps", "1,204", "Active"],
      ["Business 300", "AED 349.00", "300 / 100 Mbps", "486", "Active"],
      ["Hotspot Pro", "AED 85.00", "50 / 20 Mbps", "782", "Active"],
      ["Home 50", "AED 65.00", "50 / 10 Mbps", "374", "Disabled"],
    ],
    billing: [
      ["INV-002491", "Amir Rahman", "AED 105.00", "08 Aug 2026", "Paid"],
      ["INV-002490", "Noor Trading LLC", "AED 366.45", "12 Aug 2026", "Partially paid"],
      ["INV-002489", "Sara Khalid", "AED 68.25", "05 Aug 2026", "Overdue"],
      ["INV-002488", "Marina Café", "AED 89.25", "14 Aug 2026", "Issued"],
    ],
    vouchers: [
      ["BATCH-8291", "Hotspot Pro", "500", "318 sold", "Active"],
      ["BATCH-8290", "Home 50", "250", "250 sold", "Completed"],
      ["BATCH-8289", "Guest Day", "1,000", "741 sold", "Active"],
      ["BATCH-8288", "Business 300", "50", "0 sold", "Ready"],
    ],
    nas: [
      ["DXB-MKT-01", "MikroTik", "10.20.1.1", "486 sessions", "Online"],
      ["DXB-RAD-02", "Generic RADIUS", "10.20.2.1", "302 sessions", "Online"],
      ["AUH-MKT-01", "MikroTik", "10.30.1.1", "271 sessions", "Degraded"],
      ["SHJ-RAD-01", "Generic RADIUS", "10.40.1.1", "—", "Offline"],
    ],
    sessions: [
      ["amir.home", "DXB-MKT-01", "10.64.18.22", "2h 18m", "Active"],
      ["noor.office", "DXB-RAD-02", "10.64.21.87", "6h 42m", "Active"],
      ["guest-8291", "AUH-MKT-01", "10.65.8.11", "38m", "Active"],
      ["sara.k", "DXB-MKT-01", "10.64.19.43", "Stopped 4m ago", "Reconciled"],
    ],
    admin: [
      ["Dubai HQ", "Main site", "2,142 subscribers", "18 NAS", "Active"],
      ["Abu Dhabi", "Branch", "512 subscribers", "5 NAS", "Active"],
      ["Sharjah", "Branch", "192 subscribers", "2 NAS", "Active"],
      ["VLH entitlement", "Enterprise", "2,846 / 5,000 users", "Valid to 01 Sep", "Healthy"],
    ],
  }[page];
  if (page === "billing" && data !== undefined)
    return (
      <>
        <div className="panel table-panel">
          <Toolbar placeholder="Search billing" filters={["All status", "All sites"]} onSearch={setSearch} />
          <DeletableRecordTable
            kind="invoice"
            rows={data.billing.filter(
              (row) =>
                search === "" ||
                Object.values(row).some((value) =>
                  String(value ?? "")
                    .toLowerCase()
                    .includes(search.toLowerCase()),
                ),
            )}
            session={session}
            onChanged={onChanged}
          />
        </div>
        <div className="panel table-panel secondary-records">
          <PanelHeader title="Recorded payments" subtitle="Audited financial receipts" action="" />
          <DeletableRecordTable kind="payment" rows={data.payments} session={session} onChanged={onChanged} />
        </div>
        <ExpensePanel data={data} session={session} onChanged={onChanged}/>
      </>
    );
  if (page === "vouchers" && data !== undefined)
    return (
      <div className="panel table-panel">
        <Toolbar placeholder="Search vouchers" filters={["All status", "All sites"]} onSearch={setSearch} />
        <DeletableRecordTable kind="voucher-batch" rows={data.vouchers} session={session} onChanged={onChanged} />
      </div>
    );
  if (page === "admin" && data !== undefined) return <SettingsHub data={data} session={session} />;
  const liveRows =
    data === undefined
      ? undefined
      : {
          packages: data.packages.map((r) => [cell(r, "name"), `${cell(r, "currency")} ${(Number(cell(r, "price_minor")) / 100).toFixed(2)}`, `${Math.round(Number(cell(r, "download_bps")) / 1e6)} / ${Math.round(Number(cell(r, "upload_bps")) / 1e6)} Mbps`, cell(r, "subscriber_count"), cell(r, "status")]),
          billing: data.billing.map((r) => [cell(r, "invoice_number"), cell(r, "customer"), `${cell(r, "currency")} ${(Number(cell(r, "total_minor")) / 100).toFixed(2)}`, new Date(cell(r, "due_at")).toLocaleDateString(), cell(r, "status")]),
          vouchers: data.vouchers.map((r) => [cell(r, "id").slice(0, 8), cell(r, "package_name"), cell(r, "generated_count"), `${cell(r, "sold_count")} sold`, cell(r, "status")]),
          nas: data.nas.map((r) => [cell(r, "name"), cell(r, "adapter"), cell(r, "address"), `${cell(r, "active_sessions")} sessions`, cell(r, "status")]),
          sessions: data.sessions.map((r) => [cell(r, "principal"), cell(r, "nas_name"), cell(r, "framed_ip"), new Date(cell(r, "started_at")).toLocaleString(), cell(r, "status")]),
          admin: data.sites.map((r) => [cell(r, "name"), "Site", `${cell(r, "subscriber_count")} subscribers`, `${cell(r, "nas_count")} NAS`, cell(r, "status")]),
        }[page];
  const headers = {
    packages: ["Package", "Price", "Policy", "Subscribers", "Status"],
    billing: ["Invoice", "Customer", "Total", "Due", "Status"],
    vouchers: ["Batch", "Package", "Generated", "Progress", "Status"],
    nas: ["Device", "Adapter", "Address", "Load", "Status"],
    sessions: ["Principal", "NAS", "Framed address", "Started", "Status"],
    admin: ["Resource", "Type", "Scope", "Details", "Status"],
  }[page];
  const rows = liveRows ?? (preview ? content : []);
  return (
    <div className="panel table-panel">
      <Toolbar placeholder={`Search ${page}`} filters={["All status", "All sites"]} onSearch={setSearch} />
      {data === undefined && !preview ? <EmptyState /> : <DataTable headers={headers} rows={filterRows(rows, search)} />}
    </div>
  );
}

function filterRows(rows: readonly (readonly string[])[], search: string) {
  const term = search.trim().toLocaleLowerCase();
  return term === "" ? rows : rows.filter((row) => row.some((value) => value.toLocaleLowerCase().includes(term)));
}

function EmptyState() {
  return (
    <div className="empty-state">
      <Activity />
      <strong>Loading live workspace data</strong>
      <span>This view updates automatically.</span>
    </div>
  );
}

type CreatePage = "subscribers" | "packages" | "billing" | "payments" | "vouchers" | "nas";
function localDate(days = 0) {
  const date = new Date(Date.now() + days * 86_400_000);
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 16);
}
function CreateDialog({ page, workspace, session, onClose, onCreated }: { page: CreatePage; workspace?: WorkspaceData; session: Session; onClose: () => void; onCreated: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [expiryMode, setExpiryMode] = useState("first-use");
  const [macBinding, setMacBinding] = useState(false);
  const [accountType,setAccountType]=useState("pppoe"),[pinMode,setPinMode]=useState(false);
  const [credentialPolicy,setCredentialPolicy]=useState<CredentialPolicy>({passwordLengthEnabled:false,passwordComplexityEnabled:false,passwordMinLength:12,passwordMaxLength:256,pinMinDigits:4,pinMaxDigits:12,nasSecretMinLength:12,nasSecretMaxLength:256});
  const [voucherCodes, setVoucherCodes] = useState<string[]>();
  const [voucherPrint, setVoucherPrint] = useState<string>();
  const [voucherA4Print, setVoucherA4Print] = useState<string[]>();
  const [nasCommand,setNasCommand]=useState("");
  const [upload, setUpload] = useState("");
  const [download, setDownload] = useState("");
  const [burstPercent, setBurstPercent] = useState("10");
  const [customQueue, setCustomQueue] = useState(false);
  const [queue, setQueue] = useState<QueueForm>(() => suggestQueue("", "", "10"));
  useEffect(()=>{if(page==="subscribers"||page==="nas")void loadCredentialPolicy(session.tenantId,session.token).then(setCredentialPolicy).catch(()=>undefined);},[page,session.tenantId,session.token]);
  useEffect(() => {
    if (!customQueue) setQueue(suggestQueue(upload, download, burstPercent));
  }, [upload, download, burstPercent, customQueue]);
  function changeQueue(key: keyof QueueForm, value: string) {
    setCustomQueue(true);
    setQueue((current) => ({ ...current, [key]: value }));
  }
  function restoreQueue() {
    setCustomQueue(false);
    setQueue(suggestQueue(upload, download, burstPercent));
  }
  const title = {
    subscribers: "New subscriber",
    packages: "Create package",
    billing: "Issue invoice",
    payments: "Record payment",
    vouchers: "Generate voucher batch",
    nas: "Add NAS device",
  }[page];
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const form = Object.fromEntries(new FormData(event.currentTarget));
    try {
      let path = "";
      let body: Record<string, unknown> = {};
      if (page === "packages") {
        path = "/api/v1/packages";
        body = {
          site_id: form.site_id || undefined,
          code: String(form.code).trim().toUpperCase(),
          name: String(form.name).trim(),
          currency: form.currency,
          price_minor: Math.round(Number(form.price) * 100),
          tax_rate_basis_points: Math.round(Number(form.tax_rate) * 100),
          validity_seconds: Number(form.validity_days) * 86_400,
          upload_bps: Number(form.upload) * 1_000_000,
          download_bps: Number(form.download) * 1_000_000,
          simultaneous_use: Number(form.simultaneous_use),
          queue_policy: {
            burst_upload_bps: Number(form.burst_upload) * 1_000_000,
            burst_download_bps: Number(form.burst_download) * 1_000_000,
            burst_threshold_upload_bps: Number(form.burst_threshold_upload) * 1_000_000,
            burst_threshold_download_bps: Number(form.burst_threshold_download) * 1_000_000,
            burst_time_upload_seconds: Number(form.burst_time_upload),
            burst_time_download_seconds: Number(form.burst_time_download),
            queue_priority: Number(form.queue_priority),
            minimum_upload_bps: Number(form.minimum_upload) * 1_000_000,
            minimum_download_bps: Number(form.minimum_download) * 1_000_000,
          },
          effective_from: new Date(String(form.effective_from)).toISOString(),
        };
      } else if (page === "nas") {
        path = "/api/v1/nas";
        body = {
          site_id: form.site_id,
          code: String(form.code).trim().toUpperCase(),
          name: String(form.name).trim(),
          adapter: form.adapter,
          address: String(form.address).trim(),
          shared_secret: String(form.shared_secret),
        };
      } else if (page === "subscribers") {
        const pin=String(form.pin??"");
        path = "/api/v1/subscribers";
        body = {
          site_id: form.site_id,
          username: (pinMode?pin:String(form.username)).trim().toLowerCase(),
          display_name: String(form.display_name).trim(),
          ...(form.email ? { email: String(form.email).trim().toLowerCase() } : {}),
          ...(form.phone ? { phone: String(form.phone).trim() } : {}),
          account_type: form.account_type,
          nas_id: form.nas_id,
          package_version_id: form.package_version_id,
          credential: pinMode?pin:form.credential,
          pin_mode:pinMode,
          billing_day: Number(form.billing_day),
          portal_enabled: form.portal_enabled === "on",
          mac_binding_enabled: form.mac_binding_enabled === "on",
          ...(form.bound_mac ? { bound_mac: String(form.bound_mac).trim().toUpperCase() } : {}),
          ...(form.expires_at ? { expires_at: new Date(String(form.expires_at)).toISOString() } : {}),
        };
      } else if (page === "billing") {
        path = "/api/v1/invoices";
        body = {
          billing_account_id: form.billing_account_id,
          series: String(form.series).trim().toUpperCase(),
          currency: form.currency,
          lines: [
            {
              description: String(form.description).trim(),
              quantity: Number(form.quantity),
              unit_price_minor: Math.round(Number(form.unit_price) * 100),
              tax_rate_basis_points: Math.round(Number(form.tax_rate) * 100),
              ...(form.package_version_id ? { package_version_id: form.package_version_id } : {}),
            },
          ],
          issued_at: new Date(String(form.issued_at)).toISOString(),
          due_at: new Date(String(form.due_at)).toISOString(),
          idempotency_key: newCorrelationId(),
        };
      } else if (page === "payments") {
        path = "/api/v1/payments";
        const invoice = (workspace?.billing ?? []).find((row) => cell(row, "id") === String(form.invoice_id));
        body = {
          billing_account_id: cell(invoice ?? {}, "billing_account_id"),
          invoice_id: form.invoice_id,
          method: form.method,
          currency: form.currency,
          amount_minor: Math.round(Number(form.amount) * 100),
          ...(form.reference ? { reference: String(form.reference).trim() } : {}),
          idempotency_key: newCorrelationId(),
          received_at: new Date(String(form.received_at)).toISOString(),
        };
      } else {
        path = "/api/v1/voucher-batches";
        body = {
          site_id: form.site_id,
          package_version_id: form.package_version_id,
          nas_id: form.nas_id,
          count: Number(form.count),
          expiry_mode: form.expiry_mode,
          ...(form.expiry_mode === "fixed"
            ? {
                fixed_expires_at: new Date(String(form.fixed_expires_at)).toISOString(),
              }
            : {}),
          currency: form.currency,
          unit_price_minor: Math.round(Number(form.unit_price) * 100),
          tax_rate_basis_points: Math.round(Number(form.tax_rate) * 100),
          idempotency_key: newCorrelationId(),
        };
      }
      const result = await createResource<{
        vouchers?: { code: string }[];
        print_svg?: string;
        print_a4_svgs?: string[];
      }>(path, session.tenantId, session.token, body, session.csrf);
      if (page === "vouchers") {
        setVoucherCodes(result.vouchers?.map((item) => item.code) ?? []);
        setVoucherPrint(result.print_svg);
        setVoucherA4Print(result.print_a4_svgs);
      } else if(page==="nas"){
        const radiusAddress=String(form.radius_server_address).trim();
        const quote=(value:string)=>`"${value.replaceAll("\\","\\\\").replaceAll('"','\\"')}"`;
        setNasCommand(`/radius add address=${radiusAddress} authentication-port=1812 accounting-port=1813 service=ppp,hotspot secret=${quote(String(form.shared_secret))} comment=${quote(`CHIS ${String(form.code).trim().toUpperCase()}`)}`);
      } else onCreated();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Unable to save");
    } finally {
      setBusy(false);
    }
  }
  const sites = workspace?.sites ?? [];
  const nas = workspace?.nas ?? [];
  const packages = workspace?.packages ?? [];
  return (
    <div className="dialog-backdrop" role="presentation">
      <section className="dialog" role="dialog" aria-modal="true" aria-labelledby="dialog-title">
        <header>
          <div>
            <p className="eyebrow">VNS workspace</p>
            <h2 id="dialog-title">{nasCommand?"MikroTik RADIUS command":voucherCodes === undefined ? title : "Voucher codes ready"}</h2>
          </div>
          <button className="icon-button" onClick={voucherCodes === undefined&&!nasCommand ? onClose : onCreated} aria-label="Close">
            <X />
          </button>
        </header>
        {nasCommand ? <NasCommandResult command={nasCommand} onDone={onCreated}/>:voucherCodes !== undefined ? (
          <VoucherResult codes={voucherCodes} printSvg={voucherPrint} a4Svg={voucherA4Print} onDone={onCreated} />
        ) : (
          <form onSubmit={(event) => void submit(event)}>
            {(page === "nas" || page === "subscribers" || page === "packages" || page === "vouchers") && (
              <fieldset>
                <legend>Scope</legend>
                <label>
                  Site
                  <select name="site_id" required={page !== "packages"}>
                    <option value="">{page === "packages" ? "Available at all sites" : "Select site"}</option>
                    {sites.map((row) => (
                      <option value={cell(row, "id")} key={cell(row, "id")}>
                        {cell(row, "name")}
                      </option>
                    ))}
                  </select>
                </label>
              </fieldset>
            )}
            {page === "packages" && (
              <>
                <fieldset>
                  <legend>Package identity</legend>
                  <div className="field-pair">
                    <label>
                      Package code
                      <input name="code" required minLength={2} maxLength={32} placeholder="HOME100" />
                    </label>
                    <label>
                      Package name
                      <input name="name" required maxLength={160} placeholder="Home 100" />
                    </label>
                  </div>
                </fieldset>
                <fieldset>
                  <legend>Price and validity</legend>
                  <div className="field-pair">
                    <label>
                      Currency
                      <select name="currency" defaultValue="AED">
                        <option>AED</option>
                        <option>USD</option>
                        <option>SAR</option>
                      </select>
                    </label>
                    <label>
                      Price
                      <input name="price" type="number" min="0" step="0.01" required placeholder="100.00" />
                    </label>
                    <label>
                      Tax rate (%)
                      <input name="tax_rate" type="number" min="0" max="1000" step="0.01" defaultValue="5" required />
                    </label>
                    <label>
                      Validity (days)
                      <input name="validity_days" type="number" min="1" defaultValue="30" required />
                    </label>
                  </div>
                </fieldset>
                <fieldset>
                  <legend>Network policy</legend>
                  <div className="field-pair">
                    <label>
                      Download (Mbps)
                      <input name="download" type="number" min="0.001" step="0.001" value={download} onChange={(event) => setDownload(event.target.value)} required />
                    </label>
                    <label>
                      Upload (Mbps)
                      <input name="upload" type="number" min="0.001" step="0.001" value={upload} onChange={(event) => setUpload(event.target.value)} required />
                    </label>
                    <label>
                      Simultaneous sessions
                      <input name="simultaneous_use" type="number" min="1" max="1024" defaultValue="1" required />
                    </label>
                    <label>
                      Effective from
                      <input name="effective_from" type="datetime-local" defaultValue={localDate()} required />
                    </label>
                  </div>
                </fieldset>
                <fieldset>
                  <legend>MikroTik queue and burst</legend>
                  <p className="field-warning">Automatic mode adds the burst percentage above normal rates and sets minimum rates the same percentage below them. Thresholds track normal rates and burst time defaults to 120 seconds.</p>
                  {!customQueue ? (
                    <label className="burst-percent">
                      Burst adjustment (%)
                      <input name="burst_percent" type="number" min="1" max="100" step="1" value={burstPercent} onChange={(event) => setBurstPercent(event.target.value)} required />
                      <small>Example: 10 makes 100 Mbps burst to 110 Mbps and suggests a 90 Mbps minimum.</small>
                    </label>
                  ) : (
                    <button className="secondary queue-restore" type="button" onClick={restoreQueue}>
                      Restore automatic suggestions
                    </button>
                  )}
                  <div className="field-pair">
                    <label>
                      Burst upload (Mbps)
                      <input name="burst_upload" type="number" min="0.001" step="0.001" value={queue.burstUpload} onChange={(event) => changeQueue("burstUpload", event.target.value)} required />
                    </label>
                    <label>
                      Burst download (Mbps)
                      <input name="burst_download" type="number" min="0.001" step="0.001" value={queue.burstDownload} onChange={(event) => changeQueue("burstDownload", event.target.value)} required />
                    </label>
                    <label>
                      Upload threshold (Mbps)
                      <input name="burst_threshold_upload" type="number" min="0.001" step="0.001" value={queue.thresholdUpload} onChange={(event) => changeQueue("thresholdUpload", event.target.value)} required />
                    </label>
                    <label>
                      Download threshold (Mbps)
                      <input name="burst_threshold_download" type="number" min="0.001" step="0.001" value={queue.thresholdDownload} onChange={(event) => changeQueue("thresholdDownload", event.target.value)} required />
                    </label>
                    <label>
                      Upload burst time (seconds)
                      <input name="burst_time_upload" type="number" min="1" max="3600" value={queue.timeUpload} onChange={(event) => changeQueue("timeUpload", event.target.value)} required />
                    </label>
                    <label>
                      Download burst time (seconds)
                      <input name="burst_time_download" type="number" min="1" max="3600" value={queue.timeDownload} onChange={(event) => changeQueue("timeDownload", event.target.value)} required />
                    </label>
                    <label>
                      Queue priority (1 highest)
                      <input name="queue_priority" type="number" min="1" max="8" value={queue.priority} onChange={(event) => changeQueue("priority", event.target.value)} required />
                    </label>
                    <label>
                      Minimum upload (Mbps)
                      <input name="minimum_upload" type="number" min="0" step="0.001" value={queue.minimumUpload} onChange={(event) => changeQueue("minimumUpload", event.target.value)} required />
                    </label>
                    <label>
                      Minimum download (Mbps)
                      <input name="minimum_download" type="number" min="0" step="0.001" value={queue.minimumDownload} onChange={(event) => changeQueue("minimumDownload", event.target.value)} required />
                    </label>
                  </div>
                </fieldset>
              </>
            )}
            {page === "nas" && (
              <>
                <fieldset>
                  <legend>Device identity</legend>
                  <div className="field-pair">
                    <label>
                      Device code
                      <input name="code" required minLength={2} maxLength={32} placeholder="DXB-MKT-01" />
                    </label>
                    <label>
                      Device name
                      <input name="name" required maxLength={160} placeholder="Dubai core router" />
                    </label>
                  </div>
                  <label>
                    Adapter
                    <select name="adapter" defaultValue="mikrotik">
                      <option value="mikrotik">MikroTik</option>
                      <option value="generic-radius">Generic RADIUS</option>
                    </select>
                  </label>
                </fieldset>
                <fieldset>
                  <legend>Connectivity</legend>
                  <label>
                    IP address
                    <input name="address" required minLength={3} maxLength={64} placeholder="10.20.1.1" />
                    <small>The source IP used by the MikroTik when it sends RADIUS packets.</small>
                  </label>
                  <label>
                    CHIS RADIUS server address
                    <input name="radius_server_address" required defaultValue={window.location.hostname} placeholder="192.168.88.78" />
                    <small>The reachable CHIS server IP or hostname used in the generated RouterOS command.</small>
                  </label>
                  <label>
                    RADIUS shared secret
                    <input name="shared_secret" type="password" required minLength={credentialPolicy.nasSecretMinLength} maxLength={credentialPolicy.nasSecretMaxLength} autoComplete="new-password" />
                    <small>Encrypted before storage and never returned by the API.</small>
                  </label>
                </fieldset>
              </>
            )}
            {page === "subscribers" && (
              <>
                <fieldset>
                  <legend>Subscriber identity</legend>
                  <label>
                    Display name
                    <input name="display_name" required maxLength={160} placeholder="Customer or company name" />
                  </label>
                  <div className="field-pair">
                    <label>
                      Account type
                      <select name="account_type" value={accountType} onChange={event=>{setAccountType(event.target.value);if(event.target.value!=="hotspot")setPinMode(false);}}>
                        <option value="pppoe">PPPoE</option>
                        <option value="hotspot">Hotspot</option>
                        <option value="vpn">VPN</option>
                        <option value="wifi-guest">Wi-Fi guest</option>
                        <option value="prepaid">Prepaid</option>
                      </select>
                    </label>
                    <label>
                      Email address
                      <input name="email" type="email" maxLength={254} autoComplete="email" />
                    </label>
                    <label>
                      Phone number
                      <input name="phone" type="tel" minLength={3} maxLength={32} autoComplete="tel" placeholder="+971…" />
                    </label>
                  </div>
                </fieldset>
                <fieldset>
                  <legend>Login credentials</legend>
                  {accountType==="hotspot"&&<label className="check-field"><input type="checkbox" checked={pinMode} onChange={event=>setPinMode(event.target.checked)}/><span><strong>Use a PIN</strong><small>The numeric PIN becomes both the username and password.</small></span></label>}
                  {pinMode?<label>Hotspot PIN<input name="pin" type="password" inputMode="numeric" pattern="[0-9]+" required minLength={credentialPolicy.pinMinDigits} maxLength={credentialPolicy.pinMaxDigits} autoComplete="new-password"/><small>{credentialPolicy.pinMinDigits}–{credentialPolicy.pinMaxDigits} digits</small></label>:<div className="field-pair"><label>Username<input name="username" required autoCapitalize="none" autoComplete="username"/></label><label>Password<input name="credential" type="password" minLength={credentialPolicy.passwordLengthEnabled?credentialPolicy.passwordMinLength:undefined} maxLength={credentialPolicy.passwordLengthEnabled?credentialPolicy.passwordMaxLength:undefined} pattern={credentialPolicy.passwordComplexityEnabled?"(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9]).+":undefined} required autoComplete="new-password"/><small>{credentialPolicy.passwordLengthEnabled?`${credentialPolicy.passwordMinLength}–${credentialPolicy.passwordMaxLength} characters`:"No length rule"}{credentialPolicy.passwordComplexityEnabled?" · uppercase, lowercase, number and symbol required":" · no complexity rule"}</small></label></div>}
                </fieldset>
                <fieldset>
                  <legend>Service assignment</legend>
                  <label>
                    NAS device
                    <select name="nas_id" required>
                      <option value="">Select device</option>
                      {nas.map((row) => (
                        <option value={cell(row, "id")} key={cell(row, "id")}>
                          {cell(row, "name")}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Package
                    <select name="package_version_id" required>
                      <option value="">Select package</option>
                      {packages.map((row) => (
                        <option value={cell(row, "package_version_id")} key={cell(row, "package_version_id")}>
                          {cell(row, "name")} · {cell(row, "code")}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div className="field-pair">
                    <label>
                      Billing day
                      <input name="billing_day" type="number" min="1" max="28" defaultValue="1" required />
                    </label>
                    <label>
                      Expires at (optional)
                      <input name="expires_at" type="datetime-local" />
                    </label>
                  </div>
                </fieldset>
                <fieldset>
                  <legend>Access controls</legend>
                  <label className="check-field">
                    <input name="portal_enabled" type="checkbox" />
                    <span>
                      <strong>Enable subscriber portal</strong>
                      <small>Allow this subscriber to access the customer portal.</small>
                    </span>
                  </label>
                  <label className="check-field">
                    <input name="mac_binding_enabled" type="checkbox" checked={macBinding} onChange={(event) => setMacBinding(event.target.checked)} />
                    <span>
                      <strong>Bind service to a device MAC</strong>
                      <small>Learn the first device automatically and reject later MAC addresses.</small>
                    </span>
                  </label>
                  {macBinding && (
                    <label>
                      Initial MAC address (optional)
                      <input name="bound_mac" pattern="([0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2}" placeholder="Leave blank to learn on first connection" />
                      <small>Set this only when provisioning a known device in advance.</small>
                    </label>
                  )}
                </fieldset>
              </>
            )}
            {page === "billing" && (
              <>
                <fieldset>
                  <legend>Customer and invoice</legend>
                  <label>
                    Subscriber
                    <select name="billing_account_id" required>
                      <option value="">Select subscriber</option>
                      {(workspace?.subscribers ?? []).map((row) => (
                        <option value={cell(row, "billing_account_id")} key={cell(row, "id")}>
                          {cell(row, "display_name")} · {cell(row, "username")}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div className="field-pair">
                    <label>
                      Invoice series
                      <input name="series" required maxLength={24} defaultValue="INV" />
                    </label>
                    <label>
                      Currency
                      <select name="currency" defaultValue="AED">
                        <option>AED</option>
                        <option>USD</option>
                        <option>SAR</option>
                      </select>
                    </label>
                    <label>
                      Issue date
                      <input name="issued_at" type="datetime-local" defaultValue={localDate()} required />
                    </label>
                    <label>
                      Due date
                      <input name="due_at" type="datetime-local" defaultValue={localDate(7)} required />
                    </label>
                  </div>
                </fieldset>
                <fieldset>
                  <legend>Invoice line</legend>
                  <label>
                    Description
                    <input name="description" required maxLength={512} placeholder="Monthly internet service" />
                  </label>
                  <label>
                    Related package (optional)
                    <select name="package_version_id">
                      <option value="">No package link</option>
                      {packages.map((row) => (
                        <option value={cell(row, "package_version_id")} key={cell(row, "package_version_id")}>
                          {cell(row, "name")}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div className="field-pair">
                    <label>
                      Quantity
                      <input name="quantity" type="number" min="1" step="1" defaultValue="1" required />
                    </label>
                    <label>
                      Unit price
                      <input name="unit_price" type="number" min="0" step="0.01" required />
                    </label>
                    <label>
                      Tax rate (%)
                      <input name="tax_rate" type="number" min="0" max="1000" step="0.01" defaultValue="5" required />
                    </label>
                  </div>
                </fieldset>
              </>
            )}
            {page === "payments" && (
              <>
                <fieldset>
                  <legend>Invoice payment</legend>
                  <label>
                    Invoice
                    <select name="invoice_id" required>
                      <option value="">Select invoice</option>
                      {(workspace?.billing ?? [])
                        .filter((row) => !["paid", "void"].includes(cell(row, "status")))
                        .map((row) => (
                          <option value={cell(row, "id")} key={cell(row, "id")}>
                            {cell(row, "invoice_number")} · {cell(row, "customer")} · {cell(row, "currency")} {(Number(cell(row, "total_minor")) / 100).toFixed(2)}
                          </option>
                        ))}
                    </select>
                  </label>
                  <div className="field-pair">
                    <label>
                      Payment method
                      <select name="method" defaultValue="cash">
                        <option value="cash">Cash</option>
                        <option value="card">Card</option>
                        <option value="bank-transfer">Bank transfer</option>
                        <option value="credit">Credit</option>
                        <option value="other">Other</option>
                      </select>
                    </label>
                    <label>
                      Currency
                      <select name="currency" defaultValue="AED">
                        <option>AED</option>
                        <option>USD</option>
                        <option>SAR</option>
                      </select>
                    </label>
                    <label>
                      Amount
                      <input name="amount" type="number" min="0.01" step="0.01" required />
                    </label>
                    <label>
                      Received at
                      <input name="received_at" type="datetime-local" defaultValue={localDate()} required />
                    </label>
                  </div>
                  <label>
                    Reference (optional)
                    <input name="reference" maxLength={256} placeholder="Receipt, bank or card reference" />
                  </label>
                </fieldset>
              </>
            )}
            {page === "vouchers" && (
              <>
                <fieldset>
                  <legend>Voucher service</legend>
                  <label>
                    Package
                    <select name="package_version_id" required>
                      <option value="">Select package</option>
                      {packages.map((row) => (
                        <option value={cell(row, "package_version_id")} key={cell(row, "package_version_id")}>
                          {cell(row, "name")}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    NAS device
                    <select name="nas_id" required>
                      <option value="">Select device</option>
                      {nas.map((row) => (
                        <option value={cell(row, "id")} key={cell(row, "id")}>
                          {cell(row, "name")}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div className="field-pair">
                    <label>
                      Voucher count
                      <input name="count" type="number" min="1" max="5000" defaultValue="10" required />
                    </label>
                    <label>
                      Expiry mode
                      <select name="expiry_mode" value={expiryMode} onChange={(event) => setExpiryMode(event.target.value)}>
                        <option value="first-use">From first use</option>
                        <option value="fixed">Fixed date</option>
                      </select>
                    </label>
                  </div>
                  {expiryMode === "fixed" && (
                    <label>
                      Expires at
                      <input name="fixed_expires_at" type="datetime-local" defaultValue={localDate(30)} required />
                    </label>
                  )}
                </fieldset>
                <fieldset>
                  <legend>Sale value</legend>
                  <div className="field-pair">
                    <label>
                      Currency
                      <select name="currency" defaultValue="AED">
                        <option>AED</option>
                        <option>USD</option>
                        <option>SAR</option>
                      </select>
                    </label>
                    <label>
                      Unit price
                      <input name="unit_price" type="number" min="0" step="0.01" required />
                    </label>
                    <label>
                      Tax rate (%)
                      <input name="tax_rate" type="number" min="0" max="1000" step="0.01" defaultValue="5" required />
                    </label>
                  </div>
                </fieldset>
              </>
            )}
            {error && (
              <div className="form-error" role="alert">
                {error}
              </div>
            )}
            <footer>
              <button type="button" className="secondary" onClick={onClose}>
                Cancel
              </button>
              <button className="primary" disabled={busy}>
                {busy ? "Saving…" : "Save"}
              </button>
            </footer>
          </form>
        )}
      </section>
    </div>
  );
}

function NasCommandResult({command,onDone}:{command:string;onDone:()=>void}){
  const [copied,setCopied]=useState(false);
  async function copy(){await navigator.clipboard.writeText(command);setCopied(true);}
  return <div className="nas-command-result"><div className="result-callout"><ShieldCheck/><div><strong>NAS saved successfully</strong><p>Paste this command into the MikroTik terminal. RADIUS authentication uses UDP 1812 and accounting uses UDP 1813.</p></div></div><code>{command}</code><p className="field-warning">The router's RADIUS source IP must match the NAS IP saved in CHIS. Keep the shared secret private.</p><footer><button className="secondary" type="button" onClick={()=>void copy()}>{copied?"Copied":"Copy command"}</button><button className="primary" type="button" onClick={onDone}>Done</button></footer></div>;
}

function VoucherResult({ codes, printSvg, a4Svg, onDone }: { codes: string[]; printSvg?: string; a4Svg?: string[]; onDone: () => void }) {
  async function copy() {
    await navigator.clipboard.writeText(codes.join("\n"));
  }
  function download() {
    const url = URL.createObjectURL(new Blob([codes.join("\n") + "\n"], { type: "text/plain" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `chis-vouchers-${new Date().toISOString().slice(0, 10)}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  }
  function print(svg?: string) {
    if (svg === undefined) return;
    const url = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml" }));
    window.open(url, "_blank", "noopener");
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }
  function printA4() {
    if (a4Svg === undefined) return;
    const pages = a4Svg.map((svg) => `<section>${svg}</section>`).join("");
    const html = `<!doctype html><html><head><title>CHIS A4 vouchers</title><style>@page{size:A4;margin:0}body{margin:0}section{width:210mm;height:297mm;page-break-after:always}section:last-child{page-break-after:auto}svg{display:block;width:210mm;height:297mm}</style></head><body>${pages}<script>onload=()=>print()<\/script></body></html>`;
    const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
    window.open(url, "_blank", "noopener");
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }
  return (
    <div className="voucher-result">
      <div className="result-callout">
        <ShieldCheck />
        <div>
          <strong>Save these codes now</strong>
          <p>Plaintext voucher codes are displayed once and cannot be retrieved again.</p>
        </div>
      </div>
      <div className="voucher-codes">
        {codes.map((code) => (
          <code key={code}>{code}</code>
        ))}
      </div>
      <footer>
        <button className="secondary" onClick={() => void copy()}>
          Copy all
        </button>
        <button className="secondary" onClick={download}>
          Download .txt
        </button>
        {printSvg !== undefined && (
          <button className="secondary" onClick={() => print(printSvg)}>
            Print 80 mm
          </button>
        )}
        {a4Svg !== undefined && (
          <button className="secondary" onClick={printA4}>
            Print A4 bulk sheet ({a4Svg.length} page
            {a4Svg.length === 1 ? "" : "s"})
          </button>
        )}
        <button className="primary" onClick={onDone}>
          Done
        </button>
      </footer>
    </div>
  );
}

type SettingKey = "overview" | "workspace" | "sites" | "access" | "licensing" | "printers" | "network" | "edge" | "terminals" | "compliance" | "finance" | "pos" | "notifications" | "security" | "integrations" | "api" | "backups" | "audit" | "appearance" | "updates" | "system";
const settingGroups: {
  label: string;
  items: {
    key: SettingKey;
    label: string;
    icon: typeof Settings;
    state: "live" | string;
  }[];
}[] = [
  {
    label: "General",
    items: [
      {
        key: "overview",
        label: "Settings overview",
        icon: Settings,
        state: "live",
      },
      { key: "workspace", label: "Workspace", icon: Building2, state: "live" },
      { key: "sites", label: "Sites & branches", icon: Network, state: "live" },
      {
        key: "access",
        label: "Users & access",
        icon: UserCog,
        state: "live",
      },
      {
        key: "licensing",
        label: "Licensing",
        icon: KeyRound,
        state: "VLH gate",
      },
    ],
  },
  {
    label: "Operations",
    items: [
      { key: "printers", label: "Printers", icon: Printer, state: "live" },
      { key: "network", label: "Network & AAA", icon: Server, state: "live" },
      { key: "edge", label: "Edge Nodes", icon: Radio, state: "live" },
      { key: "terminals", label: "Android terminals", icon: CreditCard, state: "live" },
      { key: "compliance", label: "Compliance evidence", icon: ScrollText, state: "live" },
      {
        key: "finance",
        label: "Billing & tax",
        icon: CircleDollarSign,
        state: "live",
      },
      { key: "pos", label: "Vouchers & POS", icon: CreditCard, state: "live" },
      {
        key: "notifications",
        label: "Notifications",
        icon: BellRing,
        state: "live",
      },
    ],
  },
  {
    label: "Platform",
    items: [
      {
        key: "security",
        label: "Security",
        icon: ShieldCheck,
        state: "live",
      },
      {
        key: "integrations",
        label: "Integrations",
        icon: Plug,
        state: "Phase 4+",
      },
      { key: "api", label: "API & webhooks", icon: KeyRound, state: "live" },
      {
        key: "backups",
        label: "Backup & restore",
        icon: Database,
        state: "Phase 8",
      },
      {
        key: "audit",
        label: "Audit & retention",
        icon: ScrollText,
        state: "live",
      },
      {
        key: "appearance",
        label: "Appearance",
        icon: Palette,
        state: "Phase 6",
      },
      { key: "updates", label: "Updates", icon: RefreshCw, state: "Phase 8" },
      { key: "system", label: "System status", icon: Activity, state: "live" },
    ],
  },
];
const settingKeys = new Set(settingGroups.flatMap((group) => group.items.map((item) => item.key)));
function settingFromLocation(): SettingKey {
  const value = new URLSearchParams(location.search).get("settings") as SettingKey | null;
  return value !== null && settingKeys.has(value) ? value : "overview";
}
function SettingsHub({ data, session }: { data: WorkspaceData; session: Session }) {
  const [active, setActive] = useState<SettingKey>(settingFromLocation);
  useEffect(() => {
    const changed = () => setActive(settingFromLocation());
    addEventListener("popstate", changed);
    return () => removeEventListener("popstate", changed);
  }, []);
  function select(key: SettingKey) {
    setActive(key);
    const url = new URL(location.href);
    url.searchParams.set("page", "admin");
    if (key === "overview") url.searchParams.delete("settings");
    else url.searchParams.set("settings", key);
    history.pushState({ page: "admin", settings: key }, "", url);
  }
  return (
    <div className="settings-shell">
      <aside className="settings-nav" aria-label="Settings sections">
        {settingGroups.map((group) => (
          <section key={group.label}>
            <p>{group.label}</p>
            {group.items.map(({ key, label, icon: Icon, state }) => (
              <button key={key} className={active === key ? "active" : ""} onClick={() => select(key)}>
                <Icon />
                <span>{label}</span>
                <small className={state === "live" ? "ready" : "planned"}>{state === "live" ? "Live" : state}</small>
              </button>
            ))}
          </section>
        ))}
      </aside>
      <div className="settings-content">
        <SettingsPage active={active} data={data} session={session} onSelect={select} />
      </div>
    </div>
  );
}
function SettingsTitle({ title, description, state = "Live" }: { title: string; description: string; state?: string }) {
  return (
    <header className="settings-title">
      <div>
        <p className="eyebrow">Administration</p>
        <h2>{title}</h2>
        <p>{description}</p>
      </div>
      <span className={state === "Live" ? "ready" : "planned"}>{state}</span>
    </header>
  );
}
function EdgeSettings({data,session}:{data:WorkspaceData;session:Session}){
  const[edges,setEdges]=useState(data.edges),[conflicts,setConflicts]=useState(data.edgeConflicts),[result,setResult]=useState<{edge_id:string;enrollment_code:string;enrollment_expires_at:string}|null>(null),[error,setError]=useState("");
  async function create(event:FormEvent<HTMLFormElement>){event.preventDefault();setError("");const form=new FormData(event.currentTarget);try{const created=await createResource<{edge_id:string;enrollment_code:string;enrollment_expires_at:string}>("/api/v1/edge",session.tenantId,session.token,{site_id:String(form.get("site_id")),name:String(form.get("name"))},session.csrf);setResult(created);setEdges(current=>[{id:created.edge_id,site_id:String(form.get("site_id")),name:String(form.get("name")),status:"pending",sync_status:"never"},...current]);event.currentTarget.reset();}catch(reason){setError(reason instanceof Error?reason.message:"Unable to create Edge Node");}}
  async function resolveConflict(conflictId:string,status:"resolved"|"dismissed"){setError("");try{await createResource("/api/v1/edge/reconciliation",session.tenantId,session.token,{conflict_id:conflictId,status,resolution:status==="resolved"?"Reviewed and accepted central state":"Reviewed and dismissed"},session.csrf);setConflicts(current=>current.map(item=>cell(item,"id")===conflictId?{...item,status}:item));}catch(reason){setError(reason instanceof Error?reason.message:"Unable to resolve conflict");}}
  return <><SettingsTitle title="Edge Nodes" description="Enroll and monitor local offline-capable RADIUS nodes."/><div className="panel edge-settings"><form onSubmit={event=>void create(event)}><label>Site<select name="site_id" required defaultValue=""><option value="" disabled>Select site</option>{data.sites.map(site=><option key={cell(site,"id")} value={cell(site,"id")}>{cell(site,"name")}</option>)}</select></label><label>Edge name<input name="name" minLength={2} maxLength={160} required placeholder="Main site Edge"/></label><button className="primary" type="submit">Create enrollment</button></form>{error!==""&&<p className="form-error">{error}</p>}{result!==null&&<div className="connector-token"><div><strong>Enrollment details — shown once</strong><small>Use these values in the interactive chis-edge installer before {new Date(result.enrollment_expires_at).toLocaleString()}.</small><code>Tenant: {session.tenantId}{"\n"}Edge: {result.edge_id}{"\n"}Code: {result.enrollment_code}</code></div><button className="secondary" onClick={()=>void navigator.clipboard.writeText(`Tenant ID: ${session.tenantId}\nEdge Node ID: ${result.edge_id}\nEnrollment code: ${result.enrollment_code}`)}>Copy</button></div>}<div className="panel table-panel settings-table"><DataTable headers={["Edge","Host","Status","Last seen","Sync","Pending/problem events"]} rows={edges.map(edge=>[cell(edge,"name"),cell(edge,"hostname")||"—",cell(edge,"status"),cell(edge,"last_seen_at")?new Date(cell(edge,"last_seen_at")).toLocaleString():"Never",`${cell(edge,"sync_status")||"never"} / ${cell(edge,"sync_sequence")||"0"}`,cell(edge,"pending_or_problem_events")||"0"] )}/></div>{conflicts.length>0&&<div className="panel table-panel settings-table"><DataTable headers={["Conflict","Edge","Detected","Status","Action"]} rows={conflicts.map(conflict=>[cell(conflict,"conflict_type").replaceAll("_"," "),cell(conflict,"edge_name"),new Date(cell(conflict,"detected_at")).toLocaleString(),cell(conflict,"status"),["open","reviewing"].includes(cell(conflict,"status"))?<span><button className="secondary" onClick={()=>void resolveConflict(cell(conflict,"id"),"resolved")}>Resolve</button><button className="ghost" onClick={()=>void resolveConflict(cell(conflict,"id"),"dismissed")}>Dismiss</button></span>:cell(conflict,"resolution")])}/></div>}</div></>;
}
function CredentialPolicySettings({session}:{session:Session}){
  const[policy,setPolicy]=useState<CredentialPolicy|null>(null),[error,setError]=useState(""),[saved,setSaved]=useState("");
  useEffect(()=>{void loadCredentialPolicy(session.tenantId,session.token).then(setPolicy).catch(reason=>setError(reason instanceof Error?reason.message:"Unable to load credential policy"));},[session.tenantId,session.token]);
  async function save(event:FormEvent<HTMLFormElement>){event.preventDefault();if(policy===null)return;setError("");setSaved("");const value=Object.fromEntries(new FormData(event.currentTarget));try{const updated=await createResource<CredentialPolicy>("/api/v1/settings/credential-policy",session.tenantId,session.token,{password_length_enabled:policy.passwordLengthEnabled,password_complexity_enabled:policy.passwordComplexityEnabled,password_min_length:Number(value.password_min_length??policy.passwordMinLength),password_max_length:Number(value.password_max_length??policy.passwordMaxLength),pin_min_digits:Number(value.pin_min_digits),pin_max_digits:Number(value.pin_max_digits),nas_secret_min_length:Number(value.nas_secret_min_length),nas_secret_max_length:Number(value.nas_secret_max_length)},session.csrf,"PUT");setPolicy(updated);setSaved("Credential policy saved.");}catch(reason){setError(reason instanceof Error?reason.message:"Unable to save credential policy");}}
  if(policy===null)return <div className="panel credential-policy-loading"><p>{error||"Loading credential policy…"}</p></div>;
  return <form className="panel credential-policy-form" onSubmit={event=>void save(event)}><div className="credential-policy-grid"><fieldset><legend>Password requirements</legend><label className="policy-toggle"><input type="checkbox" checked={policy.passwordLengthEnabled} onChange={event=>setPolicy({...policy,passwordLengthEnabled:event.target.checked})}/><span><strong>Enforce password length</strong><small>Limit subscriber passwords to the range below.</small></span></label><div className="field-pair"><label>Password minimum<input name="password_min_length" type="number" min="1" max="256" defaultValue={policy.passwordMinLength} disabled={!policy.passwordLengthEnabled} required/></label><label>Password maximum<input name="password_max_length" type="number" min="1" max="1024" defaultValue={policy.passwordMaxLength} disabled={!policy.passwordLengthEnabled} required/></label></div><label className="policy-toggle"><input type="checkbox" checked={policy.passwordComplexityEnabled} onChange={event=>setPolicy({...policy,passwordComplexityEnabled:event.target.checked})}/><span><strong>Enforce password complexity</strong><small>Require uppercase, lowercase, number and symbol.</small></span></label></fieldset><fieldset><legend>Hotspot PIN</legend><p className="policy-description">PIN rules apply only when a hotspot subscriber uses PIN login.</p><div className="field-pair"><label>Minimum digits<input name="pin_min_digits" type="number" min="1" max="32" defaultValue={policy.pinMinDigits} required/></label><label>Maximum digits<input name="pin_max_digits" type="number" min="1" max="64" defaultValue={policy.pinMaxDigits} required/></label></div></fieldset><fieldset><legend>NAS shared secret</legend><p className="policy-description">Control the accepted length of new RADIUS shared secrets.</p><div className="field-pair"><label>Minimum characters<input name="nas_secret_min_length" type="number" min="1" max="256" defaultValue={policy.nasSecretMinLength} required/></label><label>Maximum characters<input name="nas_secret_max_length" type="number" min="1" max="1024" defaultValue={policy.nasSecretMaxLength} required/></label></div></fieldset></div><footer><div>{saved&&<p role="status" className="form-success">{saved}</p>}{error&&<p className="form-error">{error}</p>}</div><button className="primary" type="submit">Save security policy</button></footer></form>;
}
function OperatorAccessSettings({session}:{session:Session}){const [rows,setRows]=useState<Record<string,unknown>[]>([]),[selected,setSelected]=useState<Record<string,unknown>>(),[password,setPassword]=useState(""),[confirm,setConfirm]=useState(""),[error,setError]=useState(""),[notice,setNotice]=useState(""),[busy,setBusy]=useState(false);async function load(){try{setRows(await loadOperators(session.tenantId,session.token));}catch(reason){setError(reason instanceof Error?reason.message:"Unable to load operators");}}useEffect(()=>{void load();},[session.tenantId,session.token]);async function reset(event:FormEvent<HTMLFormElement>){event.preventDefault();if(selected===undefined)return;if(password!==confirm){setError("Passwords do not match.");return;}setBusy(true);setError("");try{await createResource(`/api/v1/operators/${cell(selected,"identity_id")}/password`,session.tenantId,session.token,{password},session.csrf,"PUT");setSelected(undefined);setPassword("");setConfirm("");setNotice(`Password reset for ${cell(selected,"email")}. Existing sessions were revoked.`);}catch(reason){setError(reason instanceof Error?reason.message:"Unable to reset password");}finally{setBusy(false);}}return <><section className="panel operator-access"><div className="panel-header"><div><h2>Operators</h2><p>Passwords are one-way hashed and cannot be viewed. Resetting a password revokes the operator's active sessions.</p></div></div>{notice&&<p className="form-success">{notice}</p>}{error&&<p className="form-error">{error}</p>}<div className="operator-list">{rows.map(row=><article key={cell(row,"membership_id")}><div><strong>{cell(row,"display_name")}</strong><small>{cell(row,"email")}</small></div><div><small>Roles</small><strong>{Array.isArray(row.roles)?(row.roles as string[]).join(", "):"—"}</strong></div><Status value={cell(row,"membership_status")}/><button type="button" className="secondary" onClick={()=>{setError("");setSelected(row);}}>Reset password</button></article>)}</div></section>{selected&&<div className="dialog-backdrop" role="presentation"><section className="dialog" role="dialog" aria-modal="true" aria-label="Reset operator password"><header><div><p className="eyebrow">Users & access</p><h2>Reset {cell(selected,"display_name")}</h2></div><button className="icon-button" onClick={()=>setSelected(undefined)} aria-label="Close"><X/></button></header><form onSubmit={event=>void reset(event)}><p className="field-warning">The existing password cannot be recovered. This audited reset signs the operator out everywhere; they must use the new password on their next login.</p><label>New password<input type="password" value={password} onChange={event=>setPassword(event.target.value)} required autoComplete="new-password"/></label><label>Confirm password<input type="password" value={confirm} onChange={event=>setConfirm(event.target.value)} required autoComplete="new-password"/></label>{error&&<p className="form-error">{error}</p>}<footer><button type="button" className="secondary" onClick={()=>setSelected(undefined)}>Cancel</button><button className="primary" disabled={busy||password.length===0}>{busy?"Resetting…":"Reset password"}</button></footer></form></section></div>}</>}
function OperatorAccessManagement({session,sites}:{session:Session;sites:Record<string,unknown>[]}){
  const[rows,setRows]=useState<Record<string,unknown>[]>([]),[roles,setRoles]=useState<Record<string,unknown>[]>([]),[dialog,setDialog]=useState<"invite"|"password"|"roles"|null>(null),[selected,setSelected]=useState<Record<string,unknown>>(),[error,setError]=useState(""),[notice,setNotice]=useState(""),[busy,setBusy]=useState(false);
  async function load(){try{const[result,available]=await Promise.all([loadOperators(session.tenantId,session.token),loadOperatorRoles(session.tenantId,session.token)]);setRows(result);setRoles(available);}catch(reason){setError(reason instanceof Error?reason.message:"Unable to load access settings");}}
  useEffect(()=>{void load();},[session.tenantId,session.token]);
  async function invite(event:FormEvent<HTMLFormElement>){event.preventDefault();setBusy(true);setError("");const value=Object.fromEntries(new FormData(event.currentTarget));try{await createResource("/api/v1/operators",session.tenantId,session.token,{site_id:value.site_id,email:String(value.email),display_name:String(value.display_name),password:String(value.password),role_ids:[String(value.role_id)]},session.csrf);setDialog(null);setNotice("Operator created and ready to sign in.");await load();}catch(reason){setError(reason instanceof Error?reason.message:"Unable to create operator");}finally{setBusy(false);}}
  async function resetPassword(event:FormEvent<HTMLFormElement>){event.preventDefault();if(selected===undefined)return;const value=Object.fromEntries(new FormData(event.currentTarget));if(value.password!==value.confirm){setError("Passwords do not match.");return;}setBusy(true);setError("");try{await createResource(`/api/v1/operators/${cell(selected,"identity_id")}/password`,session.tenantId,session.token,{password:String(value.password)},session.csrf,"PUT");setDialog(null);setNotice("Password reset and existing sessions revoked.");}catch(reason){setError(reason instanceof Error?reason.message:"Unable to reset password");}finally{setBusy(false);}}
  async function updateAccess(event:FormEvent<HTMLFormElement>){event.preventDefault();if(selected===undefined)return;const value=Object.fromEntries(new FormData(event.currentTarget));setBusy(true);setError("");try{await createResource(`/api/v1/operators/${cell(selected,"membership_id")}/access`,session.tenantId,session.token,{role_ids:[String(value.role_id)],reason:String(value.reason)},session.csrf,"PUT");setDialog(null);setNotice("Operator role updated.");await load();}catch(reason){setError(reason instanceof Error?reason.message:"Unable to update access");}finally{setBusy(false);}}
  async function status(row:Record<string,unknown>,to:"active"|"suspended"|"revoked"){const reason=window.prompt(`Reason to ${to} ${cell(row,"email")}`);if(reason===null)return;if(reason.trim().length<3){setError("Enter a reason of at least 3 characters.");return;}setBusy(true);setError("");try{await createResource(`/api/v1/operators/${cell(row,"membership_id")}/access`,session.tenantId,session.token,{status:to,reason:reason.trim()},session.csrf,"PUT");setNotice(`Operator ${to}.`);await load();}catch(value){setError(value instanceof Error?value.message:"Unable to update operator");}finally{setBusy(false);}}
  const open=(kind:"password"|"roles",row:Record<string,unknown>)=>{setSelected(row);setError("");setDialog(kind);};
  return <><section className="panel operator-access"><div className="panel-header"><div><h2>Operators</h2><p>Tenant-scoped memberships, roles and sessions. Passwords remain non-recoverable.</p></div><button className="primary" onClick={()=>{setError("");setDialog("invite");}}><Plus/>Add operator</button></div>{notice&&<p className="form-success">{notice}</p>}{error&&dialog===null&&<p className="form-error">{error}</p>}<div className="operator-list">{rows.map(row=><article key={cell(row,"membership_id")}><div><strong>{cell(row,"display_name")}</strong><small>{cell(row,"email")}</small></div><div><small>Roles</small><strong>{Array.isArray(row.roles)?(row.roles as string[]).join(", "):"—"}</strong></div><Status value={cell(row,"membership_status")}/><div className="operator-actions"><button className="ghost" onClick={()=>open("roles",row)}>Roles</button><button className="ghost" onClick={()=>open("password",row)}>Password</button>{cell(row,"membership_status")==="active"?<button className="ghost" disabled={busy} onClick={()=>void status(row,"suspended")}>Suspend</button>:cell(row,"membership_status")==="suspended"?<button className="ghost" disabled={busy} onClick={()=>void status(row,"active")}>Reactivate</button>:null}{cell(row,"membership_status")!=="revoked"&&<button className="ghost danger-text" disabled={busy} onClick={()=>void status(row,"revoked")}>Revoke</button>}</div></article>)}</div></section>{dialog!==null&&<div className="dialog-backdrop"><section className="dialog" role="dialog" aria-modal="true"><header><div><p className="eyebrow">Users & access</p><h2>{dialog==="invite"?"Add operator":dialog==="password"?`Reset ${cell(selected??{},"display_name")}`:`Roles for ${cell(selected??{},"display_name")}`}</h2></div><button className="icon-button" onClick={()=>setDialog(null)}><X/></button></header>{dialog==="invite"?<form onSubmit={event=>void invite(event)}><label>Name<input name="display_name" required maxLength={160}/></label><label>Email<input name="email" type="email" required/></label><label>Site<select name="site_id" required><option value="">Select site</option>{sites.map(site=><option key={cell(site,"id")} value={cell(site,"id")}>{cell(site,"name")}</option>)}</select></label><label>Role<select name="role_id" required><option value="">Select role</option>{roles.map(role=><option key={cell(role,"id")} value={cell(role,"id")}>{cell(role,"name")}</option>)}</select></label><label>Initial password<input name="password" type="password" required autoComplete="new-password"/></label>{error&&<p className="form-error">{error}</p>}<footer><button type="button" className="secondary" onClick={()=>setDialog(null)}>Cancel</button><button className="primary" disabled={busy}>Create operator</button></footer></form>:dialog==="password"?<form onSubmit={event=>void resetPassword(event)}><label>New password<input name="password" type="password" required autoComplete="new-password"/></label><label>Confirm password<input name="confirm" type="password" required autoComplete="new-password"/></label>{error&&<p className="form-error">{error}</p>}<footer><button type="button" className="secondary" onClick={()=>setDialog(null)}>Cancel</button><button className="primary" disabled={busy}>Reset password</button></footer></form>:<form onSubmit={event=>void updateAccess(event)}><label>Role<select name="role_id" required defaultValue={roles.find(role=>Array.isArray(selected?.roles)&&(selected!.roles as string[]).includes(cell(role,"name")))?.id as string|undefined}>{roles.map(role=><option key={cell(role,"id")} value={cell(role,"id")}>{cell(role,"name")}</option>)}</select></label><label>Reason<input name="reason" required minLength={3} maxLength={512}/></label>{error&&<p className="form-error">{error}</p>}<footer><button type="button" className="secondary" onClick={()=>setDialog(null)}>Cancel</button><button className="primary" disabled={busy}>Save role</button></footer></form>}</section></div>}</>;
}

function RolePermissionMatrix({session}:{session:Session}){
  const[roles,setRoles]=useState<Record<string,unknown>[]>([]),[permissions,setPermissions]=useState<Record<string,unknown>[]>([]),[selected,setSelected]=useState<Record<string,unknown>>(),[open,setOpen]=useState(false),[error,setError]=useState(""),[notice,setNotice]=useState(""),[busy,setBusy]=useState(false);
  async function load(){try{const[r,p]=await Promise.all([loadOperatorRoles(session.tenantId,session.token),loadOperatorPermissions(session.tenantId,session.token)]);setRoles(r);setPermissions(p);}catch(reason){setError(reason instanceof Error?reason.message:"Unable to load roles");}}
  useEffect(()=>{void load();},[session.tenantId,session.token]);
  function edit(role?:Record<string,unknown>){setSelected(role);setError("");setOpen(true);}
  async function save(event:FormEvent<HTMLFormElement>){event.preventDefault();const form=new FormData(event.currentTarget),permissionKeys=form.getAll("permission_key").map(String),body={name:String(form.get("name")).trim().toLowerCase(),description:String(form.get("description")).trim(),permission_keys:permissionKeys};setBusy(true);setError("");try{await createResource(selected===undefined?"/api/v1/operator-roles":`/api/v1/operator-roles/${cell(selected,"id")}`,session.tenantId,session.token,body,session.csrf,selected===undefined?"POST":"PUT");setOpen(false);setNotice(selected===undefined?"Custom role created.":"Custom role updated.");await load();}catch(reason){setError(reason instanceof Error?reason.message:"Unable to save role");}finally{setBusy(false);}}
  return <><section className="panel role-matrix"><div className="panel-header"><div><h2>Roles & permissions</h2><p>System roles are immutable. Custom roles grant only explicitly selected permissions.</p></div><button className="secondary" onClick={()=>edit()}><Plus/>New custom role</button></div>{notice&&<p className="form-success">{notice}</p>}{error&&!open&&<p className="form-error">{error}</p>}<div className="role-cards">{roles.map(role=><article key={cell(role,"id")}><div><strong>{cell(role,"name")}</strong><small>{cell(role,"description")||"No description"}</small></div><span>{Array.isArray(role.permissions)?(role.permissions as string[]).length:0} permissions</span>{role.system_role?<small>System role</small>:<button className="ghost" onClick={()=>edit(role)}>Edit permissions</button>}</article>)}</div></section>{open&&<div className="dialog-backdrop"><section className="dialog role-dialog" role="dialog" aria-modal="true"><header><div><p className="eyebrow">Role editor</p><h2>{selected===undefined?"New custom role":`Edit ${cell(selected,"name")}`}</h2></div><button className="icon-button" onClick={()=>setOpen(false)}><X/></button></header><form onSubmit={event=>void save(event)}><label>Role key<input name="name" defaultValue={selected===undefined?"":cell(selected,"name")} pattern="[a-z][a-z0-9_.-]{2,63}" required/></label><label>Description<input name="description" defaultValue={selected===undefined?"":cell(selected,"description")} maxLength={512}/></label><fieldset><legend>Permissions</legend><div className="permission-grid">{permissions.map(permission=><label key={cell(permission,"key")}><input type="checkbox" name="permission_key" value={cell(permission,"key")} defaultChecked={Array.isArray(selected?.permissions)&&(selected!.permissions as string[]).includes(cell(permission,"key"))}/><span><strong>{cell(permission,"key")}</strong><small>{cell(permission,"description")}</small></span></label>)}</div></fieldset>{error&&<p className="form-error">{error}</p>}<footer><button type="button" className="secondary" onClick={()=>setOpen(false)}>Cancel</button><button className="primary" disabled={busy}>{busy?"Saving…":"Save role"}</button></footer></form></section></div>}</>;
}

function TerminalSettings({session,sites}:{session:Session;sites:Record<string,unknown>[]}){const[rows,setRows]=useState<Record<string,unknown>[]>([]),[result,setResult]=useState<{terminalId:string;enrollmentChallenge:string;expiresAt:string}|null>(null),[error,setError]=useState(""),[busy,setBusy]=useState(false);async function load(){try{const response=await fetch(`/api/v1/terminals?tenant_id=${encodeURIComponent(session.tenantId)}`,{headers:session.token===""?{}:{authorization:`Bearer ${session.token}`},cache:"no-store"});if(!response.ok)throw new Error("Unable to load terminals");setRows(await response.json() as Record<string,unknown>[]);}catch(reason){setError(reason instanceof Error?reason.message:"Unable to load terminals");}}useEffect(()=>{void load();},[session.tenantId,session.token]);async function create(event:FormEvent<HTMLFormElement>){event.preventDefault();setBusy(true);setError("");const form=event.currentTarget,data=Object.fromEntries(new FormData(form));try{const created=await createResource<{terminalId:string;enrollmentChallenge:string;expiresAt:string}>("/api/v1/terminals/enrollments",session.tenantId,session.token,{site_id:data.site_id,name:String(data.name).trim(),serial_number:String(data.serial_number).trim(),minimum_app_version:String(data.minimum_app_version).trim()||undefined,offline_transaction_limit:Number(data.offline_transaction_limit)},session.csrf);setResult(created);form.reset();await load();}catch(reason){setError(reason instanceof Error?reason.message:"Unable to register terminal");}finally{setBusy(false);}}async function revoke(id:string){const reason=window.prompt("Reason for revoking this terminal");if(reason===null||reason.trim().length<3)return;setBusy(true);try{await createResource(`/api/v1/terminals/${id}/revoke`,session.tenantId,session.token,{reason:reason.trim()},session.csrf);await load();}catch(reason){setError(reason instanceof Error?reason.message:"Unable to revoke terminal");}finally{setBusy(false);}}return <><SettingsTitle title="Android terminals" description="Device-bound enrollment with CTM-authoritative, fail-closed security state."/><section className="panel operator-access"><div className="panel-header"><div><h2>Terminal enrollment</h2><p>Enrollment challenges and device credentials are each returned once. CTM must confirm active state before use.</p></div></div><form className="settings-form" onSubmit={event=>void create(event)}><label>Site<select name="site_id" required><option value="">Select site</option>{sites.map(site=><option key={cell(site,"id")} value={cell(site,"id")}>{cell(site,"name")}</option>)}</select></label><label>Terminal name<input name="name" minLength={2} maxLength={160} required/></label><label>Serial number<input name="serial_number" minLength={2} maxLength={160} required/></label><label>Minimum app version<input name="minimum_app_version" maxLength={64} placeholder="Optional"/></label><label>Offline operation limit<input name="offline_transaction_limit" type="number" min={0} max={500} defaultValue={0}/><small>0 disables offline transactions.</small></label><button className="primary" disabled={busy}><Plus/>Create enrollment</button></form>{error&&<p className="form-error">{error}</p>}{result&&<div className="connector-token"><div><strong>Enrollment details — shown once</strong><small>Expires {new Date(result.expiresAt).toLocaleString()}</small><code>Terminal: {result.terminalId}{"\n"}Challenge: {result.enrollmentChallenge}</code></div><button className="secondary" onClick={()=>void navigator.clipboard.writeText(`Terminal ID: ${result.terminalId}\nEnrollment challenge: ${result.enrollmentChallenge}`)}>Copy</button></div>}<div className="operator-list">{rows.map(row=><article key={cell(row,"id")}><div><strong>{cell(row,"name")}</strong><small>{cell(row,"serial_number")} · app {cell(row,"app_version")||"not enrolled"}</small></div><div><small>CTM / offline</small><strong>{cell(row,"ctm_state")} · {cell(row,"last_offline_sequence")}/{cell(row,"offline_transaction_limit")}</strong></div><Status value={cell(row,"status")}/>{cell(row,"revoked_at")===""&&<button className="ghost danger-text" disabled={busy} onClick={()=>void revoke(cell(row,"id"))}>Revoke</button>}</article>)}</div></section></>}

function ComplianceSettings({session}:{session:Session}){const[rows,setRows]=useState<Record<string,unknown>[]>([]),[total,setTotal]=useState(0),[offset,setOffset]=useState(0),[search,setSearch]=useState(""),[source,setSource]=useState(""),[selected,setSelected]=useState<Set<string>>(new Set()),[error,setError]=useState(""),[busy,setBusy]=useState(false),limit=50;async function load(nextOffset=offset){setBusy(true);setError("");try{const query=new URLSearchParams({tenant_id:session.tenantId,limit:String(limit),offset:String(nextOffset)});if(search.trim())query.set("search",search.trim());if(source.trim())query.set("source_address",source.trim());const response=await fetch(`/api/v1/compliance/records?${query}`,{headers:session.token===""?{}:{authorization:`Bearer ${session.token}`},cache:"no-store"});if(!response.ok)throw new Error("Unable to search compliance evidence");const data=await response.json() as {rows:Record<string,unknown>[];total:number};setRows(data.rows);setTotal(data.total);setOffset(nextOffset);}catch(reason){setError(reason instanceof Error?reason.message:"Unable to search evidence");}finally{setBusy(false);}}useEffect(()=>{void load(0);},[session.tenantId,session.token]);function toggle(row:Record<string,unknown>){const key=`${cell(row,"edge_id")}:${cell(row,"sequence")}`;setSelected(current=>{const next=new Set(current);if(next.has(key))next.delete(key);else next.add(key);return next;});}async function hold(){const name=window.prompt("Legal hold name"),reason=name===null?null:window.prompt("Legal hold reason");if(name===null||reason===null||name.trim().length<2||reason.trim().length<3||selected.size===0)return;setBusy(true);try{await createResource("/api/v1/compliance/holds",session.tenantId,session.token,{name:name.trim(),reason:reason.trim(),records:[...selected].map(value=>{const[edge_id,sequence]=value.split(":");return{edge_id,sequence:Number(sequence)};})},session.csrf);setSelected(new Set());await load();}catch(value){setError(value instanceof Error?value.message:"Unable to create legal hold");}finally{setBusy(false);}}async function exportEvidence(){setBusy(true);try{const data=await createResource<Record<string,unknown>>("/api/v1/compliance/exports",session.tenantId,session.token,{search:search.trim()||undefined,source_address:source.trim()||undefined},session.csrf);const url=URL.createObjectURL(new Blob([JSON.stringify(data,null,2)],{type:"application/json"})),anchor=document.createElement("a");anchor.href=url;anchor.download=`chis-compliance-${new Date().toISOString().slice(0,10)}.json`;anchor.click();URL.revokeObjectURL(url);}catch(value){setError(value instanceof Error?value.message:"Unable to export evidence");}finally{setBusy(false);}}return <><SettingsTitle title="Compliance evidence" description="Search retained Edge evidence, preserve legal holds and create checksummed exports."/><section className="panel operator-access"><form className="toolbar" onSubmit={event=>{event.preventDefault();void load(0);}}><label className="table-search"><Search/><input value={search} onChange={event=>setSearch(event.target.value)} placeholder="Search raw messages"/></label><input value={source} onChange={event=>setSource(event.target.value)} placeholder="Source address"/><button className="secondary" disabled={busy}>Search</button><button type="button" className="secondary" disabled={busy||selected.size===0} onClick={()=>void hold()}>Place legal hold ({selected.size})</button><button type="button" className="primary" disabled={busy} onClick={()=>void exportEvidence()}><Download/>Export</button></form>{error&&<p className="form-error">{error}</p>}<div className="operator-list">{rows.map(row=>{const key=`${cell(row,"edge_id")}:${cell(row,"sequence")}`;return <article key={key}><input type="checkbox" checked={selected.has(key)} onChange={()=>toggle(row)} aria-label={`Select evidence ${key}`}/><div><strong>{cell(row,"source_address")}</strong><small>{new Date(cell(row,"source_received_at")).toLocaleString()} · sequence {cell(row,"sequence")}</small></div><div><small>{cell(row,"message")}</small><small>SHA-256 {cell(row,"raw_sha256")}</small></div>{row.legal_hold?<Status value="legal hold"/>:<Status value="retained"/>}</article>})}</div><div className="pagination"><button className="secondary" disabled={offset===0||busy} onClick={()=>void load(Math.max(0,offset-limit))}>Previous</button><span>{total===0?"0 records":`${offset+1}–${Math.min(total,offset+limit)} of ${total}`}</span><button className="secondary" disabled={offset+limit>=total||busy} onClick={()=>void load(offset+limit)}>Next</button></div></section></>}

function SettingsPage({ active, data, session, onSelect }: { active: SettingKey; data: WorkspaceData; session: Session; onSelect: (key: SettingKey) => void }) {
  if (active === "overview")
    return (
      <>
        <SettingsTitle title="Settings overview" description="Configuration surfaces for this CHIS-v4 instance." />
        <div className="settings-overview">
          {settingGroups
            .flatMap((group) => group.items)
            .filter((item) => item.key !== "overview")
            .map(({ key, label, icon: Icon, state }) => (
              <button key={key} onClick={() => onSelect(key)}>
                <span>
                  <Icon />
                </span>
                <div>
                  <strong>{label}</strong>
                  <small>{state === "live" ? "Configured from live CHIS data" : `Planned · ${state}`}</small>
                </div>
                <i className={state === "live" ? "ready" : "planned"}>{state === "live" ? "Available" : "Planned"}</i>
              </button>
            ))}
        </div>
      </>
    );
  if (active === "workspace")
    return (
      <>
        <SettingsTitle title="Workspace" description="Instance identity and tenant scope used by every CHIS module." />
        <div className="settings-grid">
          <SettingValue label="Workspace ID" value={session.tenantId} />
          <SettingValue label="Deployment model" value="One CHIS instance per client site / NAS scope" />
          <SettingValue label="Sites" value={String(data.sites.length)} />
          <SettingValue label="Default timezone" value={cell(data.sites[0] ?? {}, "timezone")} />
        </div>
        <SettingNotice title="Workspace ID is automatic" text="Users do not enter or memorize this identifier. The installed CHIS instance scopes every authenticated session to its configured workspace." />
      </>
    );
  if (active === "sites")
    return (
      <>
        <SettingsTitle title="Sites & branches" description="Live operating locations and their subscriber/NAS scope." />
        <div className="panel table-panel">
          <DataTable headers={["Site", "Code", "Timezone", "Subscribers", "Status"]} rows={data.sites.map((site) => [cell(site, "name"), cell(site, "code"), cell(site, "timezone"), cell(site, "subscriber_count"), cell(site, "status")])} />
        </div>
      </>
    );
  if(active==="access")return <><SettingsTitle title="Users & access" description="Operator identities, roles, permissions and audited access lifecycle."/><OperatorAccessManagement session={session} sites={data.sites}/><RolePermissionMatrix session={session}/></>;
  if(active==="terminals")return <TerminalSettings session={session} sites={data.sites}/>;
  if(active==="compliance")return <ComplianceSettings session={session}/>;
  if (active === "printers")
    return (
      <>
        <SettingsTitle title="Printers" description="POS connectors, discovered Windows queues and secure local delivery." />
        <PrinterSettings sites={data.sites} session={session} />
      </>
    );
  if (active === "network")
    return (
      <>
        <SettingsTitle title="Network & AAA" description="Live NAS, RADIUS and session-policy configuration coverage." />
        <div className="settings-grid">
          <SettingValue label="NAS devices" value={String(data.nas.length)} />
          <SettingValue label="Active AAA sessions" value={String(data.dashboard.online_sessions)} />
          <SettingValue label="RADIUS" value="PostgreSQL projection enabled" />
          <SettingValue label="MAC binding" value="First-connect learning enabled" />
        </div>
        <div className="panel table-panel settings-table">
          <DataTable headers={["NAS", "Adapter", "Address", "Sessions", "Status"]} rows={data.nas.map((nas) => [cell(nas, "name"), cell(nas, "adapter"), cell(nas, "address"), cell(nas, "active_sessions"), cell(nas, "status")])} />
        </div>
        <RadiusLogDiagnostics session={session}/>
      </>
    );
  if (active === "edge") return <EdgeSettings data={data} session={session} />;
  if(active==="security")return <><SettingsTitle title="Security" description="Tenant credential rules for subscriber logins and NAS shared secrets."/><CredentialPolicySettings session={session}/></>;
  if (active === "finance")
    return (
      <>
        <SettingsTitle title="Billing & tax" description="Live financial defaults currently enforced by invoices and payments." />
        <div className="settings-grid">
          <SettingValue label="Default currency" value="AED" />
          <SettingValue label="Open invoices" value={String(data.dashboard.open_invoices)} />
          <SettingValue label="Open balance" value={money(data.dashboard.open_balance_minor)} />
          <SettingValue label="Ledger policy" value="Immutable double-entry evidence" />
        </div>
        <SettingNotice title="Per-package tax is active" text="Currency, price and tax rate are captured immutably on each package version and invoice line. Global tax profiles and jurisdiction templates are planned." />
      </>
    );
  if (active === "pos")
    return (
      <>
        <SettingsTitle title="Vouchers & POS" description="Design, preview and publish secure voucher print layouts." />
        <VoucherTemplateSettings sites={data.sites} session={session} />
      </>
    );
  if(active==="notifications")return <><SettingsTitle title="Notifications" description="Operational alert channels, quiet hours and event routing rules."/><NotificationSettings session={session}/></>;
  if(active==="api")return <><SettingsTitle title="API & webhooks" description="Scoped machine credentials, signed event subscriptions and delivery evidence."/><IntegrationSettings session={session}/></>;
  if(active==="audit")return <><SettingsTitle title="Audit & retention" description="Search and verify append-only audit evidence, export manifests and govern legal retention."/><AuditSettings session={session}/></>;
  if (active === "system") return <SystemSettings />;
  const plans: Record<Exclude<SettingKey, "overview" | "workspace" | "sites" | "access" | "printers" | "network" | "edge" | "terminals" | "compliance" | "finance" | "pos" | "notifications" | "api" | "audit" | "security" | "system">, { title: string; description: string; phase: string; items: string[] }> = {
    licensing: {
      title: "Licensing",
      description: "VLH authorization, entitlement capacity and node identity.",
      phase: "VLH production gate",
      items: ["Current entitlement and capacity", "Refresh history and grace state", "Node registration and credential rotation", "Production trust-key ceremony"],
    },
    integrations: {
      title: "Integrations",
      description: "External operational systems and vendor connectors.",
      phase: "Phase 4–7",
      items: ["CTM Android terminal", "VLH licensing", "Payment and messaging providers", "Additional NAS vendors"],
    },
    backups: {
      title: "Backup & restore",
      description: "Recovery policy and verified restore operations.",
      phase: "Phase 8",
      items: ["Scheduled encrypted backups", "Retention and off-site replication", "Restore validation history", "Guided disaster-recovery workflow"],
    },
    appearance: {
      title: "Appearance",
      description: "Workspace branding and operator display preferences.",
      phase: "Phase 6",
      items: ["VNS theme variants", "Logo and document branding", "Locale, timezone and number formats", "Density and accessibility preferences"],
    },
    updates: {
      title: "Updates",
      description: "Verified releases, maintenance windows and rollback history.",
      phase: "Phase 8",
      items: ["Signed release availability", "Maintenance scheduling", "Preflight and backup evidence", "Rollback and release history"],
    },
  };
  const plan = plans[active as keyof typeof plans];
  return <PlannedSettings {...plan} />;
}
function SettingValue({ label, value }: { label: string; value: string }) {
  return (
    <div className="setting-value">
      <small>{label}</small>
      <strong>{value}</strong>
    </div>
  );
}
function RadiusLogDiagnostics({session}:{session:Session}){const [entries,setEntries]=useState<string[]>(),[search,setSearch]=useState(""),[error,setError]=useState(""),[busy,setBusy]=useState(false),[source,setSource]=useState("");async function load(){setBusy(true);setError("");try{const result=await loadRadiusLogs(session.tenantId,session.token,search);setEntries(result.entries);setSource(result.source);}catch(reason){setEntries(undefined);setError(reason instanceof Error?reason.message:"Unable to load RADIUS logs");}finally{setBusy(false);}}return <section className="panel radius-diagnostics"><div><h3>FreeRADIUS diagnostics</h3><p>Restricted to the system tenant-administrator role. Sensitive credential fields are redacted.</p></div><form onSubmit={event=>{event.preventDefault();void load();}}><input aria-label="Search RADIUS logs" value={search} onChange={event=>setSearch(event.target.value)} placeholder="Search username, NAS IP or error"/><button className="secondary" disabled={busy}>{busy?"Loading…":entries===undefined?"Load logs":"Refresh"}</button></form>{error&&<p className="form-error">{error}</p>}{entries!==undefined&&<><small>{source} · latest {entries.length} matching lines</small><pre>{entries.length?entries.join("\n"):"No matching log entries."}</pre></>}</section>}
function SettingNotice({ title, text }: { title: string; text: string }) {
  return (
    <div className="setting-notice">
      <ShieldCheck />
      <div>
        <strong>{title}</strong>
        <p>{text}</p>
      </div>
    </div>
  );
}
function PlannedSettings({ title, description, phase, items }: { title: string; description: string; phase: string; items: string[] }) {
  return (
    <>
      <SettingsTitle title={title} description={description} state={`Planned · ${phase}`} />
      <div className="planned-surface">
        <div>
          <RefreshCw />
          <h3>Structure reserved</h3>
          <p>This page is included now so navigation and responsive layout remain stable as its implementation milestone lands.</p>
        </div>
        <section>
          <h3>Planned controls</h3>
          {items.map((item) => (
            <p key={item}>
              <span /> {item}
            </p>
          ))}
        </section>
      </div>
    </>
  );
}
function SystemSettings() {
  const [health, setHealth] = useState<{
    status: string;
    checks: Record<string, string>;
  }>();
  const [version, setVersion] = useState("Loading…");
  useEffect(() => {
    void Promise.all([
      fetch("/health/ready", { cache: "no-store" })
        .then((r) => r.json())
        .then(setHealth),
      fetch("/api/v1/version", { cache: "no-store" })
        .then((r) => r.json())
        .then((v: { version: string }) => setVersion(v.version)),
    ]);
  }, []);
  return (
    <>
      <SettingsTitle title="System status" description="Live readiness and installed release information." />
      <div className="settings-grid">
        <SettingValue label="Installed version" value={version} />
        <SettingValue label="Overall readiness" value={health?.status ?? "Checking"} />
        {Object.entries(health?.checks ?? {}).map(([key, value]) => (
          <SettingValue key={key} label={key.replaceAll("_", " ")} value={value} />
        ))}
      </div>
      <SettingNotice title="Operational endpoints" text="Readiness data is refreshed when this page opens and contains no connection strings or secret material." />
    </>
  );
}

type VoucherDesign = {
  companyName: string;
  heading: string;
  qrCaption: string;
  promotionHeading: string;
  promotionLink: string;
  terms: string[];
  thanksMessage: string;
  contactHeading: string;
  contactText: string;
  showLogo: boolean;
  showQr: boolean;
  showPrice: boolean;
  showValidity: boolean;
  fontScale: number;
  density: "compact" | "comfortable";
  a4Columns: number;
  a4Rows: number;
};
const initialVoucherDesign: VoucherDesign = {
  companyName: "Guest WiFi",
  heading: "Internet Access Voucher",
  qrCaption: "Please scan the above QR code to auto-login",
  promotionHeading: "Manage your account online",
  promotionLink: "www.vns.ae",
  terms: ["Please visit the portal to login", "Please keep this voucher safe until expiry", "New voucher is required if lost", "This voucher is not transferable or refundable"],
  thanksMessage: "Thank you for using our services!",
  contactHeading: "For information or contacts, please contact:",
  contactText: "info@vns.ae",
  showLogo: true,
  showQr: true,
  showPrice: true,
  showValidity: true,
  fontScale: 1,
  density: "comfortable",
  a4Columns: 2,
  a4Rows: 4,
};
function VoucherTemplateSettings({ sites, session }: { sites: Record<string, unknown>[]; session: Session }) {
  const [media, setMedia] = useState<"thermal_80mm" | "a4_bulk">("thermal_80mm"),
    [siteId, setSiteId] = useState(""),
    [name, setName] = useState("Default voucher"),
    [design, setDesign] = useState<VoucherDesign>(initialVoucherDesign),
    [preview, setPreview] = useState(""),
    [draftId, setDraftId] = useState(""),
    [status, setStatus] = useState("Not saved"),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  function update<K extends keyof VoucherDesign>(key: K, value: VoucherDesign[K]) {
    setDesign((current) => ({ ...current, [key]: value }));
  }
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const headers = {
        "content-type": "application/json",
        ...(session.token === "" ? {} : { authorization: `Bearer ${session.token}` }),
        "x-csrf-token": session.csrf,
      };
      void fetch("/api/v1/printing/voucher-templates/preview", {
        method: "POST",
        headers,
        body: JSON.stringify({
          tenant_id: session.tenantId,
          correlation_id: newCorrelationId(),
          media,
          configuration: design,
        }),
        cache: "no-store",
      })
        .then(async (response) => {
          if (!response.ok) throw new Error("Preview unavailable");
          setPreview(await response.text());
          setError("");
        })
        .catch((reason) => setError(reason instanceof Error ? reason.message : "Preview unavailable"));
    }, 180);
    return () => window.clearTimeout(timer);
  }, [media, design, session.tenantId]);
  async function save() {
    setBusy(true);
    setError("");
    try {
      const result = await createResource<{ id: string; version: number }>(
        "/api/v1/printing/voucher-templates/draft",
        session.tenantId,
        session.token,
        {
          ...(siteId === "" ? {} : { site_id: siteId }),
          media,
          name,
          configuration: design,
        },
        session.csrf,
        "PUT",
      );
      setDraftId(result.id);
      setStatus(`Draft v${result.version} saved`);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Unable to save template");
    } finally {
      setBusy(false);
    }
  }
  async function publish() {
    if (draftId === "") {
      setError("Save the draft before publishing.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await createResource(`/api/v1/printing/voucher-templates/${draftId}/publish`, session.tenantId, session.token, {}, session.csrf);
      setStatus("Published");
      setDraftId("");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Unable to publish template");
    } finally {
      setBusy(false);
    }
  }
  async function printPreview() {
    setBusy(true);
    setError("");
    const role = media === "thermal_80mm" ? "80 mm thermal" : "A4 document";
    try {
      const headers = {
        "content-type": "application/json",
        ...(session.token === "" ? {} : { authorization: `Bearer ${session.token}` }),
        "x-csrf-token": session.csrf,
      };
      const response = await fetch("/api/v1/printing/voucher-templates/preview/print", {
        method: "POST",
        headers,
        body: JSON.stringify({ tenant_id: session.tenantId, correlation_id: newCorrelationId(), ...(siteId === "" ? {} : { site_id: siteId }), media, configuration: design }),
      });
      const result = (await response.json()) as { jobId?: string; code?: string };
      if (!response.ok || result.jobId === undefined) throw new Error(result.code === `${media === "thermal_80mm" ? "thermal" : "a4"}_printer_unavailable` ? `No online ${role} printer is assigned${siteId === "" ? "" : " to this site"}.` : "Unable to queue the print preview");
      setStatus(`${role} preview queued`);
      const deadline = Date.now() + 30_000;
      while (Date.now() < deadline) {
        await new Promise((resolve) => window.setTimeout(resolve, 250));
        const jobResponse = await fetch(`/api/v1/printing/jobs/${result.jobId}?tenant_id=${encodeURIComponent(session.tenantId)}`, { headers: session.token === "" ? {} : { authorization: `Bearer ${session.token}` }, cache: "no-store" });
        if (!jobResponse.ok) throw new Error("Unable to read the print result");
        const job = (await jobResponse.json()) as { status: string; error?: string | null };
        if (job.status === "completed") {
          setStatus(`${role} preview printed`);
          return;
        }
        if (job.status === "retry" || job.status === "dead") throw new Error(job.error || `${role} printer rejected the preview`);
        setStatus(job.status === "leased" ? `${role} preview printing now` : `${role} preview waiting for connector`);
      }
      throw new Error("The connector did not confirm printing within 30 seconds");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Unable to print preview");
      setStatus("Print preview failed");
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="voucher-designer">
      <header>
        <div className="designer-tabs">
          <button className={media === "thermal_80mm" ? "active" : ""} onClick={() => setMedia("thermal_80mm")}>
            80 mm voucher
          </button>
          <button className={media === "a4_bulk" ? "active" : ""} onClick={() => setMedia("a4_bulk")}>
            A4 bulk sheet
          </button>
        </div>
        <Status value={status} />
      </header>
      <div className="designer-layout">
        <div className="designer-controls">
          <div className="field-pair">
            <label>
              Template name
              <input value={name} onChange={(event) => setName(event.target.value)} maxLength={160} />
            </label>
            <label>
              Scope
              <select value={siteId} onChange={(event) => setSiteId(event.target.value)}>
                <option value="">All sites</option>
                {sites.map((site) => (
                  <option key={cell(site, "id")} value={cell(site, "id")}>
                    {cell(site, "name")}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <fieldset>
            <legend>Brand and heading</legend>
            <label>
              Company name
              <input value={design.companyName} onChange={(event) => update("companyName", event.target.value)} />
            </label>
            <label>
              Voucher heading
              <input value={design.heading} onChange={(event) => update("heading", event.target.value)} />
            </label>
            <div className="field-pair">
              <label>
                Thank-you message
                <input value={design.thanksMessage} onChange={(event) => update("thanksMessage", event.target.value)} />
              </label>
              <label>
                QR caption
                <input value={design.qrCaption} onChange={(event) => update("qrCaption", event.target.value)} />
              </label>
            </div>
          </fieldset>
          <fieldset>
            <legend>Contact and terms</legend>
            <div className="field-pair">
              <label>
                Contact heading
                <input value={design.contactHeading} onChange={(event) => update("contactHeading", event.target.value)} />
              </label>
              <label>
                Contact details
                <input value={design.contactText} onChange={(event) => update("contactText", event.target.value)} />
              </label>
            </div>
            {design.terms.map((term, index) => (
              <label key={index}>
                Term {index + 1}
                <input
                  value={term}
                  onChange={(event) =>
                    update(
                      "terms",
                      design.terms.map((value, i) => (i === index ? event.target.value : value)),
                    )
                  }
                />
              </label>
            ))}
          </fieldset>
          <fieldset>
            <legend>Layout</legend>
            <div className="designer-toggles">
              {(
                [
                  ["showQr", "QR code"],
                  ["showPrice", "Price"],
                  ["showValidity", "Validity"],
                  ["showLogo", "Logo"],
                ] as const
              ).map(([key, label]) => (
                <label className="check-field" key={key}>
                  <input type="checkbox" checked={design[key]} onChange={(event) => update(key, event.target.checked)} />
                  <span>
                    <strong>{label}</strong>
                  </span>
                </label>
              ))}
            </div>
            <div className="field-pair">
              <label>
                Text scale
                <input type="range" min="0.8" max="1.35" step="0.05" value={design.fontScale} onChange={(event) => update("fontScale", Number(event.target.value))} />
                <small>{Math.round(design.fontScale * 100)}%</small>
              </label>
              <label>
                Density
                <select value={design.density} onChange={(event) => update("density", event.target.value as VoucherDesign["density"])}>
                  <option value="comfortable">Comfortable</option>
                  <option value="compact">Compact</option>
                </select>
              </label>
              {media === "a4_bulk" && (
                <>
                  <label>
                    Columns
                    <input type="number" min="1" max="3" value={design.a4Columns} onChange={(event) => update("a4Columns", Number(event.target.value))} />
                  </label>
                  <label>
                    Rows
                    <input type="number" min="2" max="6" value={design.a4Rows} onChange={(event) => update("a4Rows", Number(event.target.value))} />
                  </label>
                </>
              )}
            </div>
          </fieldset>
          {error && (
            <div className="form-error" role="alert">
              {error}
            </div>
          )}
          <footer>
            <button className="secondary" disabled={busy || preview === ""} onClick={printPreview}>
              <Printer /> Print preview
            </button>
            <button className="secondary" disabled={busy} onClick={() => void save()}>
              Save draft
            </button>
            <button className="primary" disabled={busy || draftId === ""} onClick={() => void publish()}>
              Publish template
            </button>
          </footer>
        </div>
        <div className={`designer-preview ${media}`}>
          <div>
            <small>LIVE PREVIEW · {media === "thermal_80mm" ? "80 MM" : "A4"}</small>
            {preview !== "" && <img src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(preview)}`} alt={`${media} voucher preview`} />}
          </div>
        </div>
      </div>
    </section>
  );
}

function PrinterSettings({ sites, session }: { sites: Record<string, unknown>[]; session: Session }) {
  const [rows, setRows] = useState<Record<string, unknown>[]>([]),
    [printers, setPrinters] = useState<Record<string, unknown>[]>([]),
    [jobs, setJobs] = useState<Record<string, unknown>[]>([]),
    [token, setToken] = useState(""),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [busy, setBusy] = useState(false);
  async function load() {
    const headers = session.token === "" ? {} : { authorization: `Bearer ${session.token}` };
    const [connectorsResponse, printersResponse,jobsResponse] = await Promise.all([fetch(`/api/v1/printing/connectors?tenant_id=${encodeURIComponent(session.tenantId)}`, { headers, cache: "no-store" }), fetch(`/api/v1/printing/printers?tenant_id=${encodeURIComponent(session.tenantId)}`, { headers, cache: "no-store" }),fetch(`/api/v1/printing/jobs?tenant_id=${encodeURIComponent(session.tenantId)}&limit=25`,{headers,cache:"no-store"})]);
    if (connectorsResponse.ok) setRows((await connectorsResponse.json()) as Record<string, unknown>[]);
    if (printersResponse.ok) setPrinters((await printersResponse.json()) as Record<string, unknown>[]);
    if (jobsResponse.ok) setJobs((await jobsResponse.json()) as Record<string, unknown>[]);
  }
  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 10_000);
    return () => window.clearInterval(timer);
  }, [session.tenantId]);
  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const data = Object.fromEntries(new FormData(event.currentTarget));
    try {
      const result = await createResource<{ token: string }>("/api/v1/printing/connectors", session.tenantId, session.token, { site_id: data.site_id, name: String(data.name).trim() }, session.csrf);
      setToken(result.token);
      await load();
      event.currentTarget.reset();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Unable to create connector");
    } finally {
      setBusy(false);
    }
  }
  async function createNetworkPrinter(event:FormEvent<HTMLFormElement>){event.preventDefault();setBusy(true);setError("");const form=event.currentTarget,data=Object.fromEntries(new FormData(form));try{await createResource("/api/v1/printing/network-printers",session.tenantId,session.token,{site_id:data.site_id,name:String(data.name).trim(),endpoint:String(data.endpoint).trim(),media:data.media,role:data.role},session.csrf);setNotice("Direct IPP printer added and assigned.");form.reset();await load();}catch(reason){setError(reason instanceof Error?reason.message:"Unable to add network printer");}finally{setBusy(false);}}
  async function testPrinter(printerId: string, role: string) {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const result = await createResource<{ jobId: string }>(`/api/v1/printing/printers/${printerId}/test`, session.tenantId, session.token, {}, session.csrf);
      setNotice(`${role} test print dispatching · ${result.jobId.slice(0, 8)}`);
      const headers = session.token === "" ? {} : { authorization: `Bearer ${session.token}` };
      const deadline = Date.now() + 30000;
      while (Date.now() < deadline) {
        await new Promise((resolve) => window.setTimeout(resolve, 250));
        const response = await fetch(`/api/v1/printing/jobs/${result.jobId}?tenant_id=${encodeURIComponent(session.tenantId)}`, { headers, cache: "no-store" });
        if (!response.ok) throw new Error("Unable to read the print result");
        const job = (await response.json()) as {
          status: string;
          error?: string | null;
        };
        if (job.status === "completed") {
          setNotice(`${role} test print accepted by the Windows spooler · ${result.jobId.slice(0, 8)}`);
          return;
        }
        if (job.status === "retry" || job.status === "dead") {
          setNotice("");
          throw new Error(job.error || `The ${role} printer rejected the test job`);
        }
        setNotice(`${role} test print ${job.status === "leased" ? "printing now" : "waiting for connector"} · ${result.jobId.slice(0, 8)}`);
      }
      throw new Error("The connector did not confirm the print within 30 seconds");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Unable to complete test print");
    } finally {
      setBusy(false);
    }
  }
  async function controlJob(jobId:string,action:"retry"|"cancel") {setBusy(true);setError("");try{await createResource(`/api/v1/printing/jobs/${jobId}/control`,session.tenantId,session.token,{action},session.csrf);setNotice(action==="retry"?"Print job queued for immediate retry.":"Print job cancelled.");await load();}catch(reason){setError(reason instanceof Error?reason.message:"Unable to control print job");}finally{setBusy(false);}}
  async function reprintJob(jobId:string){const reason=window.prompt("Reason for this audited reprint");if(reason===null)return;if(reason.trim().length<3){setError("Enter a reprint reason of at least 3 characters.");return;}setBusy(true);setError("");try{const result=await createResource<{jobId:string}>(`/api/v1/printing/jobs/${jobId}/reprint`,session.tenantId,session.token,{reason:reason.trim()},session.csrf);setNotice(`Reprint queued · ${result.jobId.slice(0,8)}`);await load();}catch(value){setError(value instanceof Error?value.message:"Unable to reprint job");}finally{setBusy(false);}}
  return (
    <section className="panel printer-settings">
      <div className="panel-header">
        <div>
          <h2>Manage printers</h2>
          <p>Use a local Windows connector or send durable jobs directly to an IPP network printer.</p>
        </div>
        <a className="primary connector-download" href="/downloads/chis-print-connector-windows-amd64.exe" download>
          <Download /> Download Windows connector (.exe)
        </a>
      </div>
      <div className="printer-layout">
        <form onSubmit={(event) => void create(event)}>
          <h3>Add POS connector</h3>
          <label>
            Location
            <select name="site_id" required>
              <option value="">Select site</option>
              {sites.map((site) => (
                <option key={cell(site, "id")} value={cell(site, "id")}>
                  {cell(site, "name")}
                </option>
              ))}
            </select>
          </label>
          <label>
            Connector name
            <input name="name" required minLength={2} maxLength={160} placeholder="Front desk POS" />
          </label>
          <button className="primary" disabled={busy}>
            <Plus /> {busy ? "Creating…" : "Create connector"}
          </button>
          {error && (
            <div className="form-error" role="alert">
              {error}
            </div>
          )}
        </form>
        <form onSubmit={event=>void createNetworkPrinter(event)}><h3>Add direct IPP printer</h3><label>Location<select name="site_id" required><option value="">Select site</option>{sites.map(site=><option key={cell(site,"id")} value={cell(site,"id")}>{cell(site,"name")}</option>)}</select></label><label>Printer name<input name="name" required minLength={2} maxLength={160} placeholder="Reception printer"/></label><label>IPP endpoint<input name="endpoint" type="url" required placeholder="http://192.168.1.50/ipp/print"/></label><label>Role<select name="role" required><option value="thermal">80 mm thermal</option><option value="a4">A4 document</option></select></label><label>Media<select name="media" required><option value="80mm">80 mm</option><option value="a4">A4</option></select></label><button className="primary" disabled={busy}><Plus/>Add network printer</button></form>
      </div>
      <div className="connector-groups">
        {rows.length === 0 ? (
          <p className="printer-empty">No connector registered yet.</p>
        ) : (
          rows.map((connector) => {
            const owned = printers.filter((printer) => cell(printer, "connector_id") === cell(connector, "id") && Array.isArray(printer.roles) && (printer.roles as string[]).length > 0);
            return (
              <section className="connector-group" key={cell(connector, "id")}>
                <header>
                  <span className={cell(connector, "status") === "online" ? "online" : ""}>
                    <Plug />
                  </span>
                  <div>
                    <h3>{cell(connector, "name")}</h3>
                    <small>
                      {owned.length} assigned {owned.length === 1 ? "printer" : "printers"} · connector {cell(connector, "version")}
                    </small>
                  </div>
                  <Status value={cell(connector, "status")} />
                </header>
                <div>
                  {owned.length === 0 ? (
                    <p className="printer-empty">No printer roles assigned. Choose the thermal and A4 queues inside this connector.</p>
                  ) : (
                    owned.map((printer) => {
                      const roles = printer.roles as string[];
                      return (
                        <article className="printer-queue" key={cell(printer, "id")}>
                          <span>
                            <Printer />
                          </span>
                          <div>
                            <strong>{cell(printer, "name")}</strong>
                            <small>
                              {cell(printer, "system_name")} · {cell(printer, "status")}
                            </small>
                          </div>
                          <div className="printer-role-actions">
                            {roles.map((role) => (
                              <button type="button" className="secondary" disabled={busy || cell(printer, "status") !== "online"} onClick={() => void testPrinter(cell(printer, "id"), role === "thermal" ? "Thermal" : "A4")} key={role}>
                                <Printer /> {role === "thermal" ? "80 mm thermal" : "A4 document"} test
                              </button>
                            ))}
                          </div>
                        </article>
                      );
                    })
                  )}
                </div>
              </section>
            );
          })
        )}
        {notice && (
          <div className="print-notice" role="status">
            {notice}
          </div>
        )}
      </div>
      <section className="connector-group print-history">
        <header><span><Printer/></span><div><h3>Recent print jobs</h3><small>Encrypted delivery history and safe recovery controls</small></div></header>
        <div>{jobs.length===0?<p className="printer-empty">No print jobs yet.</p>:jobs.map(job=><article className="printer-queue" key={cell(job,"id")}><span><Printer/></span><div><strong>{cell(job,"document_type")} · {cell(job,"printer_name")}</strong><small>{cell(job,"source_type")} · {cell(job,"created_at")} {cell(job,"last_error")===""?"":`· ${cell(job,"last_error")}`}</small></div><Status value={cell(job,"status")}/><div className="printer-role-actions">{cell(job,"status")==="completed"&&<button type="button" className="secondary" disabled={busy} onClick={()=>void reprintJob(cell(job,"id"))}>Reprint</button>}{["dead","retry"].includes(cell(job,"status"))&&<button type="button" className="secondary" disabled={busy} onClick={()=>void controlJob(cell(job,"id"),"retry")}>Retry now</button>}{["queued","retry"].includes(cell(job,"status"))&&<button type="button" className="secondary" disabled={busy} onClick={()=>void controlJob(cell(job,"id"),"cancel")}>Cancel</button>}</div></article>)}</div>
      </section>
      {token && (
        <div className="connector-token">
          <ShieldCheck />
          <div>
            <strong>Connector token — shown once</strong>
            <p>Open the downloaded app, enter this CHIS address and token, then assign its thermal and A4 queues.</p>
            <code>
              chis-print-connector-windows-amd64.exe --server {window.location.origin} --token {token}
            </code>
            <small>The app remains active in the system tray. Keep this token private.</small>
          </div>
          <button className="secondary" onClick={() => void navigator.clipboard.writeText(token)}>
            Copy token
          </button>
        </div>
      )}
    </section>
  );
}

function Toolbar({ placeholder, filters, onSearch }: { placeholder: string; filters: string[]; onSearch: (value: string) => void }) {
  return (
    <div className="toolbar">
      <label className="table-search">
        <Search />
        <input aria-label={placeholder} placeholder={placeholder} onChange={(event) => onSearch(event.target.value)} />
      </label>
      <div>
        {filters.map((filter) => (
          <button type="button" className="filter" key={filter}>
            {filter}
            <ChevronDown />
          </button>
        ))}
      </div>
    </div>
  );
}
function SubscriberToolbar({search,status,packageId,siteId,packages,sites,onSearch,onStatus,onPackage,onSite}:{search:string;status:string;packageId:string;siteId:string;packages:Record<string,unknown>[];sites:Record<string,unknown>[];onSearch:(value:string)=>void;onStatus:(value:string)=>void;onPackage:(value:string)=>void;onSite:(value:string)=>void}){
  return <div className="toolbar"><label className="table-search"><Search/><input aria-label="Search subscribers" placeholder="Search MAC, username, subscriber, package, phone…" value={search} onChange={event=>onSearch(event.target.value)}/></label><div><select className="filter" aria-label="Filter by status" value={status} onChange={event=>onStatus(event.target.value)}><option value="">All statuses</option>{["pending","active","suspended","expired","revoked"].map(value=><option value={value} key={value}>{value[0]!.toUpperCase()+value.slice(1)}</option>)}</select><select className="filter" aria-label="Filter by package" value={packageId} onChange={event=>onPackage(event.target.value)}><option value="">All packages</option>{packages.map(row=><option value={cell(row,"id")} key={cell(row,"id")}>{cell(row,"name")}</option>)}</select><select className="filter" aria-label="Filter by site" value={siteId} onChange={event=>onSite(event.target.value)}><option value="">All sites</option>{sites.map(row=><option value={cell(row,"id")} key={cell(row,"id")}>{cell(row,"name")}</option>)}</select></div></div>;
}
function TablePagination({offset,limit,total,loading,onPage}:{offset:number;limit:number;total:number;loading:boolean;onPage:(offset:number)=>void}){
  const first=total===0?0:offset+1,last=Math.min(offset+limit,total);
  return <div className="table-pagination"><span>{loading?"Loading…":`${first}–${last} of ${total}`}</span><div><button type="button" disabled={loading||offset===0} onClick={()=>onPage(Math.max(0,offset-limit))}>Previous</button><button type="button" disabled={loading||offset+limit>=total} onClick={()=>onPage(offset+limit)}>Next</button></div></div>;
}
function DataTable({ headers, rows }: { headers: readonly string[]; rows: readonly (readonly string[])[] }) {
  return (
    <div className="data-table" role="table">
      <div className="table-row table-head" role="row">
        {headers.map((header) => (
          <span role="columnheader" key={header}>
            {header}
          </span>
        ))}
      </div>
      {rows.map((row, index) => (
        <div className="table-row" role="row" key={row[0]}>
          {row.map((cell, cellIndex) => (
            <span role="cell" data-label={headers[cellIndex]} key={cellIndex}>
              {cellIndex === row.length - 1 ? <Status value={cell} /> : cellIndex === 0 ? <strong>{cell}</strong> : cell}
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}
function SubscriberTable({ rows, session, onSelect, onChanged }: { rows: Record<string, unknown>[]; session: Session; onSelect: (row: Record<string, unknown>) => void; onChanged: () => void }) {
  const headers = ["Subscriber", "Username", "Package", "Account", "Status", "Actions"];
  return (
    <div className="data-table" role="table">
      <div className="table-row table-head subscriber-table" role="row">
        {headers.map((header) => (
          <span role="columnheader" key={header}>
            {header}
          </span>
        ))}
      </div>
      {rows.map((row) => (
        <div className="table-row subscriber-table" role="row" key={cell(row, "id")}>
          <span data-label="Subscriber">
            <strong>{cell(row, "display_name")}</strong>
          </span>
          <span data-label="Username" className="subscriber-identity">
            <span className="subscriber-username-line"><strong>{cell(row, "username")}</strong><Status value={row.online===true?"Online":"Offline"} /></span>
            <small>MAC: {row.bound_mac===null||row.bound_mac===undefined?(row.mac_binding_enabled===true?"learn on next connection":"not bound"):cell(row,"bound_mac")}</small>
          </span>
          <span data-label="Package">{cell(row, "package_name")}</span>
          <span data-label="Account">{cell(row, "account_type")}</span>
          <span data-label="Status">
            <Status value={cell(row, "status")} />
          </span>
          <span data-label="Actions">
            <button className="row-action" onClick={() => onSelect(row)}>
              View & manage
            </button>
            {row.mac_binding_enabled === true && row.bound_mac !== null && <MacBindingReset row={row} session={session} onChanged={onChanged} />}
            <DeleteLifecycle kind="subscriber" row={row} session={session} onChanged={onChanged} />
          </span>
        </div>
      ))}
    </div>
  );
}
function MacBindingReset({ row, session, onChanged }: { row: Record<string, unknown>; session: Session; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  async function reset() {
    if (!window.confirm(`Reset MAC binding for ${cell(row, "username")}? The next successfully connected device will be learned.`)) return;
    setBusy(true);
    try {
      await createResource(
        `/api/v1/subscribers/${cell(row, "id")}/mac-binding/reset`,
        session.tenantId,
        session.token,
        {
          expected_version: Number(cell(row, "version")),
          reason: "Operator requested device rebind",
        },
        session.csrf,
      );
      onChanged();
    } catch (reason) {
      window.alert(reason instanceof Error ? reason.message : "Unable to reset MAC binding");
    } finally {
      setBusy(false);
    }
  }
  return (
    <button className="row-action" disabled={busy} onClick={() => void reset()}>
      {busy ? "Resetting…" : "Reset MAC"}
    </button>
  );
}
function ResourceTable({ kind, rows, session, onSelect, onChanged }: { kind: "packages" | "nas"; rows: Record<string, unknown>[]; session: Session; onSelect: (row: Record<string, unknown>) => void; onChanged: () => void }) {
  const headers = kind === "packages" ? ["Resource", "Code", "Price", "Policy", "Status", "Actions"] : ["Resource", "Code", "Address", "Load", "Status", "Actions"];
  return (
    <div className="data-table" role="table">
      <div className="table-row table-head resource-table" role="row">
        {headers.map((header) => (
          <span key={header}>{header}</span>
        ))}
      </div>
      {rows.map((row) => (
        <div className="table-row resource-table" role="row" key={cell(row, "id")}>
          <span>
            <strong>{cell(row, "name")}</strong>
          </span>
          <span>{cell(row, "code")}</span>
          <span>{kind === "packages" ? `${cell(row, "currency")} ${(Number(cell(row, "price_minor")) / 100).toFixed(2)}` : cell(row, "address")}</span>
          <span>{kind === "packages" ? `${Math.round(Number(cell(row, "download_bps")) / 1e6)} / ${Math.round(Number(cell(row, "upload_bps")) / 1e6)} Mbps` : `${cell(row, "active_sessions")} sessions`}</span>
          <span>
            <Status value={cell(row, "status")} />
          </span>
          <span>
            <button className="row-action" onClick={() => onSelect(row)}>
              View & manage
            </button>
            <DeleteLifecycle kind={kind === "packages" ? "package" : "nas"} row={row} session={session} onChanged={onChanged} />
          </span>
        </div>
      ))}
    </div>
  );
}

function DeleteLifecycle({ kind, row, session, onChanged }: { kind: "subscriber" | "package" | "nas"; row: Record<string, unknown>; session: Session; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  async function remove() {
    const label = cell(row, "name") !== "—" ? cell(row, "name") : cell(row, "username");
    if (!window.confirm(`Delete ${label}? This audited action cannot be undone from the UI.`)) return;
    setBusy(true);
    try {
      const plural = kind === "package" ? "packages" : kind === "nas" ? "nas" : "subscribers";
      const id = cell(row, "id");
      let version = Number(cell(row, "version"));
      const status = cell(row, "status");
      if (kind === "subscriber" && status === "active") {
        const result = await createResource<{ version: number }>(
          `/api/v1/subscribers/${id}/transitions`,
          session.tenantId,
          session.token,
          {
            expected_version: version,
            to: "revoked",
            reason: "Operator deleted record",
          },
          session.csrf,
        );
        version = result.version;
      } else if (kind !== "subscriber" && status === "active") {
        const result = await createResource<{ version: number }>(
          `/api/v1/${plural}/${id}/transitions`,
          session.tenantId,
          session.token,
          {
            expected_version: version,
            to: "disabled",
            reason: "Operator deleted record",
          },
          session.csrf,
        );
        version = result.version;
      }
      await createResource(
        `/api/v1/${plural}/${id}/transitions`,
        session.tenantId,
        session.token,
        {
          expected_version: version,
          to: "archived",
          reason: "Operator deleted record",
        },
        session.csrf,
      );
      onChanged();
    } catch (reason) {
      window.alert(reason instanceof Error ? reason.message : "Unable to delete record");
    } finally {
      setBusy(false);
    }
  }
  return (
    <button className="row-action destructive" disabled={busy} onClick={() => void remove()}>
      {busy ? "Deleting…" : "Delete"}
    </button>
  );
}

function DeletableRecordTable({ kind, rows, session, onChanged }: { kind: "invoice" | "payment" | "voucher-batch"; rows: Record<string, unknown>[]; session: Session; onChanged: () => void }) {
  const invoice = kind === "invoice",
    payment = kind === "payment";
  const headers = invoice ? ["Invoice", "Customer", "Total", "Due", "Status", "Actions"] : payment ? ["Payment", "Customer", "Amount", "Received", "Status", "Actions"] : ["Batch", "Package", "Generated", "Progress", "Status", "Actions"];
  async function remove(row: Record<string, unknown>) {
    const label = invoice ? cell(row, "invoice_number") : payment ? (cell(row, "reference") !== "—" ? cell(row, "reference") : cell(row, "id").slice(0, 8)) : cell(row, "id").slice(0, 8);
    if (!window.confirm(`Delete ${label}? Financial and audit evidence will be retained.`)) return;
    try {
      const path = invoice ? `/api/v1/invoices/${cell(row, "id")}/void` : payment ? `/api/v1/payments/${cell(row, "id")}/refund` : `/api/v1/voucher-batches/${cell(row, "id")}/cancel`;
      await createResource(
        path,
        session.tenantId,
        session.token,
        {
          reason: "Operator deleted record",
          ...(payment ? { amount_minor: Number(cell(row, "amount_minor")) } : {}),
        },
        session.csrf,
      );
      onChanged();
    } catch (reason) {
      window.alert(reason instanceof Error ? reason.message : "Unable to delete record");
    }
  }
  return (
    <div className="data-table">
      <div className="table-row table-head deletable-table">
        {headers.map((value) => (
          <span key={value}>{value}</span>
        ))}
      </div>
      {rows.map((row) => (
        <div className="table-row deletable-table" key={cell(row, "id")}>
          <span>
            <strong>{invoice ? cell(row, "invoice_number") : payment ? (cell(row, "reference") !== "—" ? cell(row, "reference") : cell(row, "id").slice(0, 8)) : cell(row, "id").slice(0, 8)}</strong>
          </span>
          <span>{invoice || payment ? cell(row, "customer") : cell(row, "package_name")}</span>
          <span>{invoice ? `${cell(row, "currency")} ${(Number(cell(row, "total_minor")) / 100).toFixed(2)}` : payment ? `${cell(row, "currency")} ${(Number(cell(row, "amount_minor")) / 100).toFixed(2)}` : cell(row, "generated_count")}</span>
          <span>{invoice ? new Date(cell(row, "due_at")).toLocaleDateString() : payment ? new Date(cell(row, "received_at")).toLocaleString() : `${cell(row, "sold_count")} sold`}</span>
          <span>
            <Status value={cell(row, "status")} />
          </span>
          <span>
            {invoice && (
              <a className="row-action" href={`/api/v1/invoices/${cell(row, "id")}/render?tenant_id=${encodeURIComponent(session.tenantId)}`} target="_blank" rel="noreferrer">
                Print
              </a>
            )}
            <button className="row-action destructive" onClick={() => void remove(row)}>
              Delete
            </button>
          </span>
        </div>
      ))}
    </div>
  );
}
function CredentialControl({resourceType,resourceId,session}:{resourceType:"subscriber"|"nas";resourceId:string;session:Session}){
  const [credential,setCredential]=useState<string>();
  const [next,setNext]=useState("");
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState("");
  const [notice,setNotice]=useState("");
  async function reveal(){setBusy(true);setError("");setNotice("");try{setCredential(await revealCredential(resourceType,resourceId,session.tenantId,session.token));}catch(reason){setError(reason instanceof Error?reason.message:"Unable to reveal credential");}finally{setBusy(false);}}
  async function change(event:FormEvent<HTMLFormElement>){event.preventDefault();setBusy(true);setError("");setNotice("");try{await createResource(`/api/v1/credentials/${resourceType}/${resourceId}`,session.tenantId,session.token,{credential:next},session.csrf,"PUT");setCredential(next);setNext("");setNotice("Credential changed. Update the NAS or subscriber device before the next login.");}catch(reason){setError(reason instanceof Error?reason.message:"Unable to change credential");}finally{setBusy(false);}}
  return <section className="credential-control"><h3>{resourceType==="nas"?"RADIUS shared secret":"Login credential"}</h3><p>Viewing and changing this value is permission-controlled and recorded in the audit log.</p>{credential===undefined?<button type="button" className="secondary" disabled={busy} onClick={()=>void reveal()}>{busy?"Loading…":"View credential"}</button>:<div className="credential-reveal"><code>{credential}</code><span><button type="button" className="secondary" onClick={()=>void navigator.clipboard.writeText(credential)}>Copy</button><button type="button" className="ghost" onClick={()=>setCredential(undefined)}>Hide</button></span></div>}<form onSubmit={event=>void change(event)}><label>New {resourceType==="nas"?"shared secret":"password or PIN"}<input type="password" value={next} onChange={event=>setNext(event.target.value)} required autoComplete="new-password"/></label><button className="secondary" disabled={busy||next.length===0}>Change credential</button></form>{notice&&<p className="form-success">{notice}</p>}{error&&<p className="form-error">{error}</p>}</section>;
}
function ResourceDrawer({ kind, resource, session, onClose, onChanged }: { kind: "packages" | "nas"; resource: Record<string, unknown>; session: Session; onClose: () => void; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const status = cell(resource, "status");
  const options = status === "active" ? ["disabled"] : status === "disabled" ? ["active", "archived"] : [];
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const form = Object.fromEntries(new FormData(event.currentTarget));
    try {
      await createResource(
        `/api/v1/${kind}/${cell(resource, "id")}/transitions`,
        session.tenantId,
        session.token,
        {
          expected_version: Number(cell(resource, "version")),
          to: form.to,
          reason: String(form.reason).trim(),
        },
        session.csrf,
      );
      onChanged();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Unable to change status");
    } finally {
      setBusy(false);
    }
  }
  const packageFields = [
    ["Code", "code"],
    ["Price", "price_minor"],
    ["Currency", "currency"],
    ["Subscribers", "subscriber_count"],
    ["Download bps", "download_bps"],
    ["Upload bps", "upload_bps"],
    ["Burst download bps", "burst_download_bps"],
    ["Burst upload bps", "burst_upload_bps"],
    ["Download threshold", "burst_threshold_download_bps"],
    ["Upload threshold", "burst_threshold_upload_bps"],
    ["Burst time down/up", "burst_time_download_seconds"],
    ["Queue priority", "queue_priority"],
    ["Minimum download bps", "minimum_download_bps"],
    ["Minimum upload bps", "minimum_upload_bps"],
  ];
  return (
    <div
      className="drawer-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <aside className="detail-drawer" role="dialog" aria-modal="true">
        <header>
          <div>
            <p className="eyebrow">{kind === "packages" ? "Package catalogue" : "Network access server"}</p>
            <h2>{cell(resource, "name")}</h2>
            <Status value={status} />
          </div>
          <button className="icon-button" onClick={onClose}>
            <X />
          </button>
        </header>
        <section className="detail-grid">
          {(kind === "packages"
            ? packageFields
            : [
                ["Code", "code"],
                ["Adapter", "adapter"],
                ["Address", "address"],
                ["Active sessions", "active_sessions"],
                ["Updated", "updated_at"],
                ["Version", "version"],
              ]
          ).map(([label, key]) => (
            <div key={key}>
              <small>{label}</small>
              <strong>{key === "price_minor" ? (Number(cell(resource, key)) / 100).toFixed(2) : cell(resource, key)}</strong>
            </div>
          ))}
        </section>
        {kind==="nas"&&<CredentialControl resourceType="nas" resourceId={cell(resource,"id")} session={session}/>}
        {options.length > 0 && (
          <form onSubmit={(event) => void submit(event)}>
            <fieldset>
              <legend>Lifecycle action</legend>
              <label>
                New status
                <select name="to">
                  {options.map((value) => (
                    <option value={value} key={value}>
                      {value[0]!.toUpperCase() + value.slice(1)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Reason
                <input name="reason" required minLength={3} maxLength={512} placeholder="Reason for this audited change" />
              </label>
              {kind === "nas" && Number(cell(resource, "active_sessions")) > 0 && <p className="field-warning">Disable/archive is blocked while this NAS has active sessions.</p>}
              <button className="primary wide" disabled={busy || (kind === "nas" && Number(cell(resource, "active_sessions")) > 0)}>
                Apply status change
              </button>
            </fieldset>
          </form>
        )}
        {error && <div className="form-error">{error}</div>}
      </aside>
    </div>
  );
}
function formatBytes(value: string) {
  const bytes = Number(value);
  if (!Number.isFinite(bytes)) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let amount = bytes,
    index = 0;
  while (amount >= 1024 && index < units.length - 1) {
    amount /= 1024;
    index++;
  }
  return `${amount.toFixed(index < 2 ? 0 : 2)} ${units[index]}`;
}
function formatDuration(value:string){const seconds=Math.max(0,Number(value)||0),days=Math.floor(seconds/86400),hours=Math.floor(seconds%86400/3600),minutes=Math.floor(seconds%3600/60);return days>0?`${days}d ${hours}h`:hours>0?`${hours}h ${minutes}m`:`${minutes}m`;}
function formatTimestamp(value:unknown){if(value===null||value===undefined)return "—";const parsed=new Date(String(value));return Number.isNaN(parsed.getTime())?"—":parsed.toLocaleString("en-AE");}
function formatConnectionDay(value:string){const day=value.slice(0,10),parsed=new Date(`${day}T12:00:00`);return Number.isNaN(parsed.getTime())?value:parsed.toLocaleDateString("en-AE",{weekday:"long",day:"numeric",month:"long",year:"numeric"});}
function formatSessionTime(value:unknown){if(value===null||value===undefined)return "—";const parsed=new Date(String(value));return Number.isNaN(parsed.getTime())?"—":parsed.toLocaleTimeString("en-AE",{hour:"2-digit",minute:"2-digit",second:"2-digit"});}
function AaaSessionTable({ rows, onSelect }: { rows: Record<string, unknown>[]; onSelect: (row: Record<string, unknown>) => void }) {
  return (
    <div className="data-table">
      <div className="table-row table-head session-table">
        {["Principal", "NAS", "Address", "Usage", "Status", "Actions"].map((value) => (
          <span key={value}>{value}</span>
        ))}
      </div>
      {rows.map((row) => (
        <div className="table-row session-table" key={cell(row, "id")}>
          <span>
            <strong>{cell(row, "principal")}</strong>
            <small>{cell(row, "acct_session_id")}</small>
          </span>
          <span>{cell(row, "nas_name")}</span>
          <span>{cell(row, "framed_ip")}</span>
          <span>
            ↓ {formatBytes(cell(row, "output_octets"))} · ↑ {formatBytes(cell(row, "input_octets"))}
          </span>
          <span>
            <Status value={cell(row, "status")} />
          </span>
          <span>
            <button className="row-action" onClick={() => onSelect(row)}>
              Inspect
            </button>
          </span>
        </div>
      ))}
    </div>
  );
}
function AaaSessionDrawer({ value, session, onClose, onChanged }: { value: Record<string, unknown>; session: Session; onClose: () => void; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const controllable = ["active", "stale"].includes(cell(value, "status"));
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const form = Object.fromEntries(new FormData(event.currentTarget));
    try {
      await createResource(
        `/api/v1/aaa-sessions/${cell(value, "id")}/control`,
        session.tenantId,
        session.token,
        {
          command: form.command,
          reason: String(form.reason).trim(),
          idempotency_key: newCorrelationId(),
        },
        session.csrf,
      );
      onChanged();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Unable to queue session control");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div
      className="drawer-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <aside className="detail-drawer">
        <header>
          <div>
            <p className="eyebrow">AAA session</p>
            <h2>{cell(value, "principal")}</h2>
            <Status value={cell(value, "status")} />
          </div>
          <button className="icon-button" onClick={onClose}>
            <X />
          </button>
        </header>
        <section className="usage-summary">
          <div>
            <small>Downloaded</small>
            <strong>{formatBytes(cell(value, "output_octets"))}</strong>
          </div>
          <div>
            <small>Uploaded</small>
            <strong>{formatBytes(cell(value, "input_octets"))}</strong>
          </div>
        </section>
        <section className="detail-grid">
          {[
            ["Session ID", "acct_session_id"],
            ["NAS", "nas_name"],
            ["Framed address", "framed_ip"],
            ["Calling station", "calling_station_id"],
            ["Started", "started_at"],
            ["Last interim", "last_interim_at"],
            ["Stopped", "stopped_at"],
            ["Termination", "termination_cause"],
          ].map(([label, key]) => (
            <div key={key}>
              <small>{label}</small>
              <strong>{key.endsWith("_at") && cell(value, key) !== "—" ? new Date(cell(value, key)).toLocaleString() : cell(value, key)}</strong>
            </div>
          ))}
        </section>
        {controllable && (
          <form onSubmit={(event) => void submit(event)}>
            <fieldset>
              <legend>Session control</legend>
              <label>
                Command
                <select name="command">
                  <option value="disconnect">Disconnect session</option>
                  <option value="coa">Reapply policy (CoA)</option>
                </select>
              </label>
              <label>
                Reason
                <input name="reason" required minLength={3} maxLength={512} placeholder="Reason for this audited network action" />
              </label>
              <p className="field-warning">The command is queued for the trusted NAS worker and does not claim success until acknowledged.</p>
              <button className="primary wide" disabled={busy}>
                Queue command
              </button>
            </fieldset>
          </form>
        )}
        {error && <div className="form-error">{error}</div>}
      </aside>
    </div>
  );
}
function SubscriberDrawer({ subscriber, packages, session, onClose, onChanged, onMacReset }: { subscriber: Record<string, unknown>; packages: Record<string, unknown>[]; session: Session; onClose: () => void; onChanged: () => void; onMacReset: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [historyOpen,setHistoryOpen]=useState(false);
  const [connections,setConnections]=useState<Record<string,unknown>[]>([]),[connectionTotal,setConnectionTotal]=useState(0),[connectionBusy,setConnectionBusy]=useState(false),[connectionError,setConnectionError]=useState("");
  const connectionLimit=25,subscriberId=cell(subscriber,"id");
  async function loadConnections(offset:number,replace=false){setConnectionBusy(true);setConnectionError("");try{const result=await loadSubscriberConnections(session.tenantId,session.token,subscriberId,connectionLimit,offset);setConnections(current=>replace?result.data:[...current,...result.data]);setConnectionTotal(result.total);}catch(reason){setConnectionError(reason instanceof Error?reason.message:"Unable to load connection history");}finally{setConnectionBusy(false);}}
  useEffect(()=>{setConnections([]);setConnectionTotal(0);if(historyOpen)void loadConnections(0,true);},[subscriberId,session.tenantId,session.token,historyOpen]);
  const connectionDays=useMemo(()=>{const grouped=new Map<string,Record<string,unknown>[]>();for(const row of connections){const day=cell(row,"connection_day"),items=grouped.get(day)??[];items.push(row);grouped.set(day,items);}return [...grouped.entries()];},[connections]);
  const status = cell(subscriber, "status");
  const allowed: Record<string, string[]> = {
    pending: ["active", "revoked", "archived"],
    active: ["suspended", "expired", "revoked"],
    suspended: ["active", "expired", "revoked", "archived"],
    expired: ["active", "revoked", "archived"],
    revoked: ["archived"],
  };
  async function transition(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const form = Object.fromEntries(new FormData(event.currentTarget));
    try {
      await createResource(
        `/api/v1/subscribers/${cell(subscriber, "id")}/transitions`,
        session.tenantId,
        session.token,
        {
          expected_version: Number(cell(subscriber, "version")),
          to: form.to,
          reason: String(form.reason).trim(),
        },
        session.csrf,
      );
      onChanged();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Unable to update subscriber");
    } finally {
      setBusy(false);
    }
  }
  async function changePackage(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const form = Object.fromEntries(new FormData(event.currentTarget));
    try {
      await createResource(
        `/api/v1/subscribers/${cell(subscriber, "id")}/package-changes`,
        session.tenantId,
        session.token,
        {
          package_version_id: form.package_version_id,
          effective_at: new Date(String(form.effective_at)).toISOString(),
          proration_policy: form.proration_policy,
          reason: String(form.reason).trim(),
        },
        session.csrf,
      );
      onChanged();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Unable to request package change");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div
      className="drawer-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <aside className={`detail-drawer subscriber-detail-drawer${historyOpen?" history-expanded":""}`} role="dialog" aria-modal="true" aria-label={`Manage ${cell(subscriber, "display_name")}`}>
        <button className="session-bookmark" type="button" aria-expanded={historyOpen} aria-controls="subscriber-connection-history" onClick={()=>setHistoryOpen(value=>!value)}><span>{historyOpen?"Hide sessions":"Sessions"}</span><b aria-hidden="true">{historyOpen?"›":"‹"}</b></button>
        <header>
          <div>
            <p className="eyebrow">Subscriber record</p>
            <h2>{cell(subscriber, "display_name")}</h2>
            <span>
              <Status value={status} />
            </span>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="Close">
            <X />
          </button>
        </header>
        <section className="detail-grid">
          <div>
            <small>Username</small>
            <strong>{cell(subscriber, "username")}</strong>
          </div>
          <div>
            <small>Account type</small>
            <strong>{cell(subscriber, "account_type")}</strong>
          </div>
          <div>
            <small>Package</small>
            <strong>{cell(subscriber, "package_name")}</strong>
          </div>
          <div>
            <small>NAS</small>
            <strong>{cell(subscriber, "nas_name")}</strong>
          </div>
          <div>
            <small>Email</small>
            <strong>{cell(subscriber, "email")}</strong>
          </div>
          <div>
            <small>Phone</small>
            <strong>{cell(subscriber, "phone")}</strong>
          </div>
        </section>
        <section className="subscriber-access" aria-label="Network access and usage">
          <div className="subscriber-access-title">
            <div><p className="eyebrow">Network access</p><h3>Connection and usage</h3></div>
            <Status value={subscriber.online===true?"Online":"Offline"}/>
          </div>
          <div className="subscriber-usage-cards">
            <div><Download/><small>Total downloaded</small><strong>{formatBytes(cell(subscriber,"total_download_octets"))}</strong></div>
            <div><Activity/><small>Total uploaded</small><strong>{formatBytes(cell(subscriber,"total_upload_octets"))}</strong></div>
            <div><Radio/><small>Sessions</small><strong>{cell(subscriber,"session_count")}</strong></div>
            <div><Gauge/><small>Connected time</small><strong>{formatDuration(cell(subscriber,"total_session_seconds"))}</strong></div>
          </div>
          <div className="subscriber-network-grid">
            <div><small>Session state</small><strong>{cell(subscriber,"session_status")}</strong></div>
            <div><small>Session ID</small><strong>{cell(subscriber,"acct_session_id")}</strong></div>
            <div><small>Assigned IP</small><strong>{cell(subscriber,"framed_ip")}</strong></div>
            <div className="device-mac-detail"><small>Device MAC</small><strong>{subscriber.bound_mac===null||subscriber.bound_mac===undefined?cell(subscriber,"calling_station_id"):cell(subscriber,"bound_mac")}</strong>{subscriber.mac_binding_enabled===true&&subscriber.bound_mac!==null&&subscriber.bound_mac!==undefined&&<MacBindingReset row={subscriber} session={session} onChanged={onMacReset}/>}</div>
            <div><small>NAS</small><strong>{cell(subscriber,"nas_name")} · {cell(subscriber,"nas_address")}</strong></div>
            <div><small>Session started</small><strong>{formatTimestamp(subscriber.session_started_at)}</strong></div>
            <div><small>Last accounting update</small><strong>{formatTimestamp(subscriber.session_last_update??subscriber.session_stopped_at)}</strong></div>
            <div><small>Termination cause</small><strong>{cell(subscriber,"termination_cause")}</strong></div>
            <div><small>Current download</small><strong>{formatBytes(cell(subscriber,"session_download_octets"))}</strong></div>
            <div><small>Current upload</small><strong>{formatBytes(cell(subscriber,"session_upload_octets"))}</strong></div>
            <div><small>Package speed</small><strong>↓ {formatBytes(cell(subscriber,"download_bps"))}/s · ↑ {formatBytes(cell(subscriber,"upload_bps"))}/s</strong></div>
            <div><small>Simultaneous sessions</small><strong>{cell(subscriber,"simultaneous_use")}</strong></div>
          </div>
        </section>
        {historyOpen&&<section id="subscriber-connection-history" className="connection-history" aria-label="Previous connections by day">
          <div className="connection-history-title">
            <div><p className="eyebrow">Connection history</p><h3>Previous connections by day</h3></div>
            <small>{connectionTotal} session{connectionTotal===1?"":"s"} recorded</small>
          </div>
          {connectionError && <div className="form-error">{connectionError}</div>}
          {!connectionBusy&&connectionDays.length===0&&!connectionError&&<div className="connection-history-empty">No previous connections have been recorded.</div>}
          {connectionDays.map(([day,rows])=>{const seconds=rows.reduce((sum,row)=>sum+Number(cell(row,"duration_seconds")||0),0),downloaded=rows.reduce((sum,row)=>sum+Number(cell(row,"output_octets")||0),0),uploaded=rows.reduce((sum,row)=>sum+Number(cell(row,"input_octets")||0),0);return <div className="connection-day-group" key={day}>
            <div className="connection-day-summary"><strong>{formatConnectionDay(day)}</strong><small>{rows.length} sessions · {formatDuration(String(seconds))} · ↓ {formatBytes(String(downloaded))} · ↑ {formatBytes(String(uploaded))}</small></div>
            <div className="compact-session-table" role="table" aria-label={`Sessions for ${formatConnectionDay(day)}`}>
              <div className="compact-session-row compact-session-head" role="row"><span>Start</span><span>End</span><span>Duration</span><span>IP / MAC</span><span>NAS</span><span>Traffic ↓ / ↑</span><span>Status / cause</span></div>
              {rows.map(row=><div className="compact-session-row" role="row" key={cell(row,"id")}><span>{formatSessionTime(row.started_at)}</span><span>{row.stopped_at?formatSessionTime(row.stopped_at):"Current"}</span><span>{formatDuration(cell(row,"duration_seconds"))}</span><span><b>{cell(row,"framed_ip")}</b><small>{cell(row,"calling_station_id")}</small></span><span><b>{cell(row,"nas_name")}</b><small>{cell(row,"nas_address")}</small></span><span><b>↓ {formatBytes(cell(row,"output_octets"))}</b><small>↑ {formatBytes(cell(row,"input_octets"))}</small></span><span><Status value={cell(row,"status")}/><small>{cell(row,"termination_cause")}</small></span></div>)}
            </div>
          </div>;})}
          {connectionBusy&&<div className="connection-history-empty">Loading connections…</div>}
          {connections.length<connectionTotal&&!connectionBusy&&<button className="secondary wide" type="button" onClick={()=>void loadConnections(connections.length)}>Load older connections</button>}
        </section>}
        <CredentialControl resourceType="subscriber" resourceId={cell(subscriber,"id")} session={session}/>
        {(allowed[status]?.length ?? 0) > 0 && (
          <form onSubmit={(event) => void transition(event)}>
            <fieldset>
              <legend>Lifecycle action</legend>
              <label>
                New status
                <select name="to" required>
                  {allowed[status]!.map((value) => (
                    <option value={value} key={value}>
                      {value[0]!.toUpperCase() + value.slice(1)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Reason
                <input name="reason" required minLength={3} maxLength={512} placeholder="Reason for this audited change" />
              </label>
              <button className="primary wide" disabled={busy}>
                Apply status change
              </button>
            </fieldset>
          </form>
        )}
        <form onSubmit={(event) => void changePackage(event)}>
          <fieldset>
            <legend>Change package</legend>
            <label>
              New package
              <select name="package_version_id" required>
                <option value="">Select package</option>
                {packages
                  .filter((row) => cell(row, "package_version_id") !== cell(subscriber, "package_version_id"))
                  .map((row) => (
                    <option value={cell(row, "package_version_id")} key={cell(row, "package_version_id")}>
                      {cell(row, "name")}
                    </option>
                  ))}
              </select>
            </label>
            <div className="field-pair">
              <label>
                Effective at
                <input name="effective_at" type="datetime-local" defaultValue={localDate()} required />
              </label>
              <label>
                Proration
                <select name="proration_policy" defaultValue="next-cycle">
                  <option value="next-cycle">Next cycle</option>
                  <option value="immediate">Immediate</option>
                  <option value="none">No proration</option>
                </select>
              </label>
            </div>
            <label>
              Reason
              <input name="reason" required minLength={3} maxLength={512} />
            </label>
            <button className="secondary wide" disabled={busy}>
              Request package change
            </button>
          </fieldset>
        </form>
        {error && (
          <div className="form-error" role="alert">
            {error}
          </div>
        )}
      </aside>
    </div>
  );
}
function SubscriberRows({rows,onOpen}:{rows:Record<string,unknown>[];onOpen:()=>void}) {
  return (
    <div className="subscriber-rows compact">
      {rows.length===0&&<div className="dashboard-empty">No subscriber activity yet.</div>}
      {rows.map((subscriber) => (
        <button type="button" className="subscriber-row dashboard-subscriber" key={cell(subscriber,"id")} onClick={onOpen}>
          <span className="person-avatar">{cell(subscriber,"display_name").split(/\s+/).slice(0,2).map(value=>value[0]).join("").toUpperCase()}</span>
          <span className="subscriber-name">
            <strong>{cell(subscriber,"display_name")}</strong>
            <small>{cell(subscriber,"username")}</small>
          </span>
          <span data-label="Package">{cell(subscriber,"package_name")}</span>
          <Status value={subscriber.online===true?"Online":"Offline"} />
          <Status value={cell(subscriber,"status")} />
          <span className="more" aria-hidden="true">
            •••
          </span>
        </button>
      ))}
    </div>
  );
}
function Status({ value }: { value: string }) {
  const tone = /active|online|paid|healthy|completed|ready|reconciled/i.test(value) ? "success" : /pending|issued|partial|degraded/i.test(value) ? "warning" : /offline|overdue|suspended/i.test(value) ? "danger" : "neutral";
  return (
    <span className={`status ${tone}`}>
      <i />
      {value}
    </span>
  );
}
function Kpi({ label, value, delta, icon, tone, chart }: { label: string; value: string; delta: string; icon: ReactNode; tone: string; chart: string }) {
  return (
    <section className="kpi">
      <div className={`kpi-icon ${tone}`}>{icon}</div>
      <p>{label}</p>
      <strong>{value}</strong>
      <span>{delta}</span>
      <div className="meter">
        <i style={{ width: chart }} />
      </div>
    </section>
  );
}
function PanelHeader({ title, subtitle, action, onAction }: { title: string; subtitle: string; action: string; onAction?:()=>void }) {
  return (
    <div className="panel-header">
      <div>
        <h2>{title}</h2>
        <p>{subtitle}</p>
      </div>
      <button type="button" onClick={onAction} disabled={onAction===undefined}>
        {action} <span>→</span>
      </button>
    </div>
  );
}
function TrafficChart({download,upload}:{download:number;upload:number}) {
  const maximum=Math.max(download,upload,1);
  return (
    <div className="traffic-volume" aria-label="Transferred data during the last 24 hours">
      <div><span>Download</span><strong>{dataSize(download)}</strong><i><b style={{width:`${download/maximum*100}%`}} /></i></div>
      <div><span>Upload</span><strong>{dataSize(upload)}</strong><i><b className="upload" style={{width:`${upload/maximum*100}%`}} /></i></div>
      <div className="traffic-note">
        <Activity />
        <span>Calculated from authoritative RADIUS accounting updates received during the last 24 hours.</span>
      </div>
    </div>
  );
}
function HealthRing({percent}:{percent:number}) {
  return (
    <div className="health-ring" style={{background:`conic-gradient(var(--green) 0 ${percent}%,#e9eef4 ${percent}% 100%)`}}>
      <div>
        <strong>{percent}%</strong>
        <span>healthy</span>
      </div>
    </div>
  );
}
function Health({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div>
      <span>
        <i className={color} />
        {label}
      </span>
      <strong>{value}</strong>
    </div>
  );
}
function LiveActivityFeed({events}:{events:LiveEvent[]}) {
  const items=events.slice(0,5);
  return <div className="activity-feed">
    {items.length===0&&<div className="dashboard-empty">Waiting for live tenant activity.</div>}
    {items.map(item=><div key={item.eventId}>
      <span className="activity-icon"><Activity /></span>
      <span><strong>{item.eventType.replaceAll("."," ")}</strong><small>Tenant event #{item.sequence.toLocaleString()}</small></span>
      <time>Live</time>
    </div>)}
  </div>;
}
function ActivityFeed() {
  const items = [
    {
      icon: <Users />,
      text: "Subscriber activated",
      detail: "amir.home · Dubai HQ",
      time: "Now",
    },
    {
      icon: <Activity />,
      text: "Accounting reconciled",
      detail: "DXB-MKT-01 · 128 events",
      time: "2m",
    },
    {
      icon: <CircleDollarSign />,
      text: "Payment received",
      detail: "AED 366.45 · INV-002490",
      time: "6m",
    },
    {
      icon: <Network />,
      text: "NAS health degraded",
      detail: "AUH-MKT-01 · packet loss",
      time: "9m",
    },
  ];
  return (
    <div className="activity-feed">
      {items.map((item) => (
        <div key={item.text}>
          <span className="activity-icon">{item.icon}</span>
          <span>
            <strong>{item.text}</strong>
            <small>{item.detail}</small>
          </span>
          <time>{item.time}</time>
        </div>
      ))}
    </div>
  );
}
function MobileNav({ active, onNavigate }: { active: Page; onNavigate: (page: Page) => void }) {
  const items = nav.slice(0, 4);
  return (
    <nav className="mobile-nav">
      {items.map(({ page, label, icon: Icon }) => (
        <button className={active === page ? "active" : ""} onClick={() => onNavigate(page)} key={page}>
          <Icon />
          <span>{label.split(" ")[0]}</span>
        </button>
      ))}
      <button onClick={() => onNavigate("admin")}>
        <Menu />
        <span>More</span>
      </button>
    </nav>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
